<template>
  <div class="ai-chat-page" :style="pageStyle">
    <!-- 顶部状态条 -->
    <div v-if="sessionStatus === 'waiting_review'" class="status-bar status-review">
      <van-icon name="info-o" /> 资料审核中，请耐心等待
    </div>
    <div v-if="sessionStatus === 'expired'" class="status-bar status-expired" @click="handleReEnter">
      <van-icon name="clock-o" /> 会话已过期，点击重新进入
    </div>

    <!-- 订单展示模式 -->
    <div v-if="activeOrderList.length > 0" class="order-display-mode">
      <div class="order-mode-header">您有进行中的预约订单</div>
      <OrderCard
        v-for="(order, index) in sortedOrderList"
        :key="index"
        :order="order"
      />
    </div>

    <!-- 聊天模式 -->
    <div v-else class="chat-container">
      <div class="message-list" ref="messageListRef" :style="messageListStyle">
        <div
          v-for="(msg, index) in messageList"
          :key="index"
          :id="'msg-' + index"
          class="message-wrapper"
          :class="msg.type"
        >
          <component
            :is="getMessageComponent(msg)"
            :msg="msg"
            :index="index"
            @choice="handleChoice"
            @fetch-dates="handleFetchDates"
            @slot-confirm="handleSlotConfirm"
            @preview="previewImage"
          />
        </div>
      </div>

      <!-- 底部输入区 -->
      <div class="input-bar" :style="inputBarStyle">
        <div class="input-row">
          <van-field
            v-model="inputValue"
            placeholder="请输入您的问题..."
            :disabled="isInputDisabled"
            @focus="onInputFocus"
            @keyup.enter="sendText"
          />
          <van-button type="primary" size="small" :disabled="isInputDisabled || !inputValue.trim()" @click="sendText">发送</van-button>
          <van-button class="func-btn" size="small" @click="toggleFuncPanel">
            <van-icon :name="isShowFuncPanel ? 'arrow-down' : 'plus'" />
          </van-button>
        </div>

        <!-- 功能面板 -->
        <FuncPanel
          v-if="isShowFuncPanel"
          @upload="triggerImageUpload"
          @hint="requestHint"
          @restart="handleRestart"
          @goback="handleGoBack"
          @orders="goOrders"
        />
      </div>
    </div>

    <!-- 隐藏的文件选择，用于图片上传 -->
    <input
      type="file"
      accept="image/*"
      ref="fileInputRef"
      style="display: none;"
      @change="onFileChange"
    />

    <!-- 日期选择弹窗 -->
    <van-calendar
      v-model="calendarShow"
      :min-date="minDate"
      :max-date="maxDate"
      :formatter="calendarFormatter"
      @confirm="onCalendarConfirm"
    />

    <!-- 车牌号/车挂号确认（车头/车挂行驶证正页 OCR 后） -->
    <van-dialog
      v-model="showPlateConfirm"
      :title="plateConfirmTitle"
      :show-cancel-button="false"
      confirm-button-text="确定"
      :before-close="onPlateConfirm"
    >
      <div style="padding: 16px;">
        <p style="margin: 0 0 12px 0; color: #666;">请核对识别的{{ plateConfirmLabel }}，如有误请修改</p>
        <van-field v-model="confirmPlateNo" :label="plateConfirmLabel" :placeholder="`请输入${plateConfirmLabel}`" />
      </div>
    </van-dialog>

    <!-- 油脂车辆引导：去丽天车队预约（跳转小程序） -->
    <van-dialog
      v-model="showOilGuideDialog"
      title="提示"
      :show-cancel-button="false"
      confirm-button-text="取消"
      @opened="onOilGuideDialogOpened"
    >
      <div style="padding: 16px;">
        <p style="margin: 0 0 12px 0; color: #666;">检测到您的车辆属于丽天车队业务，请前往小程序完成预约</p>
        <div id="oil-guide-btn-box" style="min-height: 44px; display: flex; align-items: center; justify-content: center;">
          <span v-if="oilGuideWxState === 'error'" style="color: #ee0a24; font-size: 14px;">{{ oilGuideWxError }}</span>
        </div>
      </div>
    </van-dialog>

    <!-- 图片预览 -->
    <van-image-preview v-model="previewShow" :images="previewImages" :start-position="previewIndex" />
  </div>
</template>

<script>
import { Toast, Dialog } from 'vant';
import Compressor from 'compressorjs';
import WebSocketClient from '@/utils/websocket';
import { ensureWxConfig, mountLaunchButton } from '@/utils/wxLaunchWeapp';
import { erpDialogApi } from '@/fetch/dialogApi';
import api from '@/fetch/api';
import { presignImage } from '@/fetch/file';
import TextMessage from './components/TextMessage';
import ImageMessage from './components/ImageMessage';
import ChoiceMessage from './components/ChoiceMessage';
import DatePickerMessage from './components/DatePickerMessage';
import TimeSlotMessage from './components/TimeSlotMessage';
import OrderCard from './components/OrderCard';
import FuncPanel from './components/FuncPanel';

// 当前 ERP 对话的标准步骤顺序（用于 goBack 时清掉目标 step 之后的旧消息，避免
// 后端 push 的下一步 botReply 被 dedup 命中到旧的、已 verified 的同 currentStep 消息）。
// 注意：仅作用于 messageList 本地清理，不影响后端会话状态。
const ERP_STEP_ORDER = [
  'confirm_head',
  'vehicle_license_front',
  'vehicle_license_back',
  'vehicle_license_copy',
  'confirm_trailer',
  'trailer_license_front',
  'trailer_license_back',
  'trailer_license_copy',
  'driver_license_front',
  'choose_date',
  'choose_time',
  'choose_plan',
];

const GO_BACK_KEYWORDS = ['上一步', '返回', '返回上一步', '重新选择', '重来', 'back', 'prev', '拍错了', '刚才拍错了', '重新拍', '重拍', '换一张', '拍错了重新来', '不对', '错了', '重新上传', '重来一遍', '返回去', '退一步'];

export default {
  name: 'AIChatIndex',
  components: {
    TextMessage,
    ImageMessage,
    ChoiceMessage,
    DatePickerMessage,
    TimeSlotMessage,
    OrderCard,
    FuncPanel,
  },
  data() {
    return {
      sessionId: '',
      sessionStatus: '', // bot / waiting_review / closed / expired
      messageList: [],
      inputValue: '',
      isShowFuncPanel: false,
      isSubmitting: false,
      keyboardHeight: 0,
      jobType: null, // 0=提货, 1=卸货
      selectedDate: '',
      orderList: [],
      wsClient: null,
      reconnectCount: 0,
      isConnecting: false,
      isSessionIniting: false,
      reviewPollTimer: null,
      recentMsgKeys: new Set(),
      promptedMsgIndices: new Set(),
      lastActionTime: 0,
      slotAutoRefreshCount: 0,
      calendarShow: false,
      calendarMsgIndex: null,
      minDate: new Date(),
      maxDate: new Date(2099, 11, 31),
      previewShow: false,
      previewImages: [],
      previewIndex: 0,
      initialHeight: 0,
      onResize: null,
      // 车牌确认弹框（车头行驶证 OCR 后）
      showPlateConfirm: false,
      confirmPlateNo: '',
      pendingErpOcr: null, // { ocrData, sourceStep, ossUrl, plateNo }
      pendingTempMsg: null,
      // 【P4】会话内已识别出的车头/车挂号，queryErpPlans 兜底使用
      lastKnownHeadNo: '',    // 例如 "苏BL0580"
      lastKnownTrailerNo: '', // 例如 "苏BT693"
      // 【P7】会话内用户选中的计划单 tkh（提货扣），getpdxxnew 需要用它
      lastKnownPlanThk: '',
      // 老司机点击“更换车头”后，重新上传新行驶证正页查计划时只查车头，不带旧车挂号
      isChangingHead: false,
      // 【P5】uploadAndOCR 上传前用 getSession 拿后端真实 currentStep 作为 sourceStep。
      //   解决 detectErpSourceStep 读 messageList 不可靠（planInfo=null 时 processErpOcr 不推进）的 bug。
      sessionCurrentStep: '',  // 后端最新 currentStep（权威）
      // 图片签名缓存：原 URL -> 签名 URL（仅当前会话内有效，详见 fetch/file.js presignImages）
      imageUrlMap: new Map(),
      // 油脂车辆引导弹窗（查不到计划且车头档案 sfyz=1 时弹出，跳转丽天车队小程序）
      showOilGuideDialog: false,
      oilGuideWxState: 'loading',
      oilGuideWxError: '',
    };
  },
  computed: {
    isInputDisabled() {
      return this.sessionStatus === 'expired';
    },
    activeOrderList() {
      const statuses = [1, 2, 5, 6, 7];
      return (this.orderList || []).filter((o) => {
        const status = typeof o.status === 'number' ? o.status : Number(o.status);
        return statuses.includes(status);
      });
    },
    sortedOrderList() {
      const list = this.activeOrderList.map((o, i) => ({ ...o, __key: i }));
      list.sort((a, b) => {
        const ad = this.toMs(a.deadline);
        const bd = this.toMs(b.deadline);
        if (ad === bd) return a.__key - b.__key;
        if (ad === null) return 1;
        if (bd === null) return -1;
        return ad - bd;
      });
      return list;
    },
    pageStyle() {
      if (this.keyboardHeight > 0) {
        return { paddingBottom: `${this.keyboardHeight + 30}px` };
      }
      return {};
    },
    inputBarStyle() {
      if (this.keyboardHeight > 0) {
        return { transform: `translateY(-${this.keyboardHeight}px)` };
      }
      return {};
    },
    chatApi() {
      return erpDialogApi;
    },
    plateConfirmTitle() {
      if (this.pendingErpOcr && this.pendingErpOcr.sourceStep.startsWith('trailer_license_')) {
        return '确认车挂号';
      }
      return '确认车牌号';
    },
    plateConfirmLabel() {
      if (this.pendingErpOcr && this.pendingErpOcr.sourceStep.startsWith('trailer_license_')) {
        return '车挂号';
      }
      return '车牌号';
    },
    messageListStyle() {
      // 功能区展开时增加底部留白，避免内容被面板遮挡
      const bottom = this.isShowFuncPanel ? 240 : 80;
      return { paddingBottom: `calc(${bottom}px + env(safe-area-inset-bottom))` };
    },
  },
  created() {
    // 对话页只能从首页「提卸货」进入（见 panelTpl.handlePuhuoFlow），这里直接写死标题
    document.title = '仓储智能对话预约';
    const cachedJobType = localStorage.getItem('ai_job_type');
    if (cachedJobType !== null && cachedJobType !== '') {
      this.jobType = Number(cachedJobType);
    }
    if (!this.checkLogin()) return;
    this.tryResumeSession();
  },
  mounted() {
    this.listenKeyboard();
  },
  beforeDestroy() {
    this.closeWebSocket();
    this.stopReviewPolling();
    window.removeEventListener('resize', this.onResize);
  },
  methods: {
    // ---------- 登录与初始化 ----------
    checkLogin() {
      const openid = this.$store.state.wx_info.openid;
      const mobile = this.$store.state.wx_info.mobile;
      return !!(openid && mobile);
    },
    getUserInfo() {
      return {
        openid: this.$store.state.wx_info.openid,
        phone: this.$store.state.wx_info.mobile,
        username: this.$store.state.wx_info.username || this.$store.state.wx_info.nickname,
        userId: this.$store.state.userid,
      };
    },
    clearCache() {
      localStorage.removeItem('ai_session_id');
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        if (key.startsWith('sessionObj_')) localStorage.removeItem(key);
        if (key.startsWith('ai_plan_thk_')) localStorage.removeItem(key);
        if (key.startsWith('ai_head_no_')) localStorage.removeItem(key);
        if (key.startsWith('ai_trailer_no_')) localStorage.removeItem(key);
        if (key.startsWith('ai_changing_head_')) localStorage.removeItem(key);
        if (key.startsWith('ai_current_job_type_')) localStorage.removeItem(key);
      });
      localStorage.removeItem('sessionObj');
    },
    // ---------- 会话 ----------
    // 统一构造 createErpSession 的入参：
    //   1) 读 localStorage 里的 ai_template_code / ai_force_new_session
    //      （首页 panelTpl.handlePuhuoFlow 老司机场景会写：erp_old_driver + force=1）
    //   2) ai_force_new_session 读后立即清除（避免污染下次进入）
    //   3) extraForce=true 时强制带 force=true，给"重新开始"等明确要新建会话的场景用
    // 修复：之前只有 tryResumeSession 走这套逻辑，initSession 完全没读 ai_template_code，
    //   导致老司机点"重新开始" → initSession(true) → 后端收不到 templateCode → 退回 erp_new_driver。
    buildErpSessionParams(extraForce = false) {
      const user = this.getUserInfo();
      const templateCode = localStorage.getItem('ai_template_code') || undefined;
      const forceNewFlag = localStorage.getItem('ai_force_new_session');
      const forceNew = extraForce || forceNewFlag === '1';
      if (forceNewFlag !== null) {
        localStorage.removeItem('ai_force_new_session');
      }
      const params = {
        openid: user.openid || '',
        clientInfo: JSON.stringify({ platform: 'h5' }),
        jobType: this.jobType,
      };
      if (templateCode) params.templateCode = templateCode;
      if (forceNew) params.force = true;
      return params;
    },
    async tryResumeSession() {
      // ERP：直接通过创建接口复用同一 openid 的会话
      const user = this.getUserInfo();
      if (!user.openid) return;
      try {
        // 【P-老司机断点续约】panelTpl.handlePuhuoFlow 老司机分支会写
        //   ai_force_new_session='1' 强制新会话。但用户"再次进入页面"期望复用旧会话保留进度。
        //   在 AIChatIndex 入口主动清除 force 标记，让 createErpSession 按 openid 复用进行中会话。
        //   新司机不受影响：panelTpl 新司机分支本来就是 removeItem 不写 '1'，这里 removeItem 是空操作。
        const templateCode = localStorage.getItem('ai_template_code');
        if (templateCode === 'erp_old_driver') {
          localStorage.removeItem('ai_force_new_session');
        }
        const sessionParams = this.buildErpSessionParams();
        const res = await erpDialogApi.createErpSession(sessionParams);
        if (res.code === 200 && res.data) {
          const data = res.data;
          if (data.expireTime && Date.now() > data.expireTime) {
            Dialog.alert({ message: '会话已过期，请重新进入' });
            return;
          }

          const sessionStatus = (data.status || 'bot').toLowerCase();
          const isFlowCompleted = sessionStatus === 'waiting_review' ||
            (data.templateCode === 'order_display' && Array.isArray(data.orderList));
          const storedJobType = localStorage.getItem(`ai_current_job_type_${user.openid}`);
          const jobTypeChanged = storedJobType !== null && storedJobType !== '' && Number(storedJobType) !== this.jobType;

          // 资料已提交审核中 / 已有展示订单：不管作业类型是否变化，都继续展示，不重新开始
          if (isFlowCompleted) {
            console.log('[tryResumeSession] 当前流程已结束/审核中，继续展示，忽略作业类型变化');
          } else if (jobTypeChanged) {
            // 流程中作业类型变化：强制新建会话
            console.log('[tryResumeSession] 作业类型变化', storedJobType, '->', this.jobType, '强制新建会话');
            if (data.sessionId) {
              this.chatApi.closeSession(data.sessionId, user.openid).catch(() => {});
            }
            this.sessionId = '';
            this.sessionStatus = '';
            this.messageList = [];
            this.initSession(true);
            return;
          }

          this.sessionId = data.sessionId;
          this.sessionStatus = sessionStatus;
          // 复用会话时同样记录当前作业类型，用于下次进入判断
          if (user.openid && this.jobType !== null) {
            localStorage.setItem(`ai_current_job_type_${user.openid}`, String(this.jobType));
          }
          this.messageList = [];
          this.recentMsgKeys.clear();
          this.promptedMsgIndices.clear();
          this.isChangingHead = false;
          // 复用旧会话时恢复上次选中的提货口（按 sessionId 隔离）
          this.lastKnownPlanThk = localStorage.getItem(`ai_plan_thk_${this.sessionId}`) || '';
          // 复用旧会话时恢复上次确认的车头/车挂号，副页交叉校验需要
          // 先按 sessionId 取，取不到再按 openid 取（应对会话 ID 变化）
          this.lastKnownHeadNo = localStorage.getItem(`ai_head_no_${this.sessionId}`) || '';
          this.lastKnownTrailerNo = localStorage.getItem(`ai_trailer_no_${this.sessionId}`) || '';
          if (!this.lastKnownHeadNo || !this.lastKnownTrailerNo) {
            this.lastKnownHeadNo = this.lastKnownHeadNo || localStorage.getItem(`ai_head_no_${user.openid}`) || '';
            this.lastKnownTrailerNo = this.lastKnownTrailerNo || localStorage.getItem(`ai_trailer_no_${user.openid}`) || '';
          }
          // 上次点了"更换车头"后退出的场景：恢复标记并清掉旧车牌，防止重新进入后用旧车挂号查计划
          // 用 openid 作 key，避免会话 ID 变化导致标记丢失
          const changingHeadFlag = localStorage.getItem(`ai_changing_head_${user.openid}`);
          if (changingHeadFlag === '1') {
            this.isChangingHead = true;
            this.lastKnownHeadNo = '';
            this.lastKnownTrailerNo = '';
            console.log('[tryResumeSession] 恢复更换车头状态，已清掉旧车头/车挂');
          }

          // 订单展示模式直接渲染订单列表
          if (data.templateCode === 'order_display' && Array.isArray(data.orderList)) {
            this.orderList = data.orderList;
            return;
          }
          this.orderList = [];

          // 【P-老司机断点续约】复用旧会话时 messageList 是空，需要拉历史 messages 渲染
          await this.fetchHistoryMessages();
          await this.connectWebSocket(data.wsUrl);
          if (this.sessionStatus === 'waiting_review') this.startReviewPolling();
          return;
        }
      } catch (err) {
        // 网络/CORS 等请求层失败：不能落到下面的 initSession()，
        // 否则会立刻重发同一个 createErpSession（一次进入连发两次请求，后端看到连续两条 OPTIONS）。
        // 只有拿到响应后的业务性失败（无可用会话）才允许兜底新建。
        console.error('[ERP Session] 恢复失败', err);
        Toast.clear();
        Toast.fail('网络异常，请稍后重试');
        return;
      }
      this.initSession();
    },
    // 拉 /session/{sessionId} 拿当前 currentStep。
    // 老司机断点续约用到：复用旧会话时同步 sessionCurrentStep，让 WebSocket 重连后能按"当前步骤"
    // 推送对应的 bot 消息（带选项/按钮的正常样式）。
    // 注意：这里**不渲染历史 messages**。历史消息里含模板占位符 + 已完成的步骤 + 用户提交的 value（"0"/"1"）
    //   等多种内容，渲染会和 WebSocket 推送的"当前步骤"消息重复 / 错位 / 走样。
    //   用户的期望是"只展示当前步骤到页面，样式和正常流程一样"——由 WebSocket 接管渲染。
    async fetchHistoryMessages() {
      const user = this.getUserInfo();
      if (!user.openid || !this.sessionId) return;
      try {
        const sessResp = await erpDialogApi.getSession(this.sessionId, user.openid);
        if (sessResp && sessResp.code === 200 && sessResp.data && sessResp.data.currentStep) {
          this.sessionCurrentStep = sessResp.data.currentStep;
        }
      } catch (e) {
        console.warn('[fetchHistoryMessages] getSession 失败', e);
      }
    },
    async initSession(forceNew = false) {
      if (this.isSessionIniting || this.sessionId) return;
      this.isSessionIniting = true;
      Toast.loading({ message: '连接中...', duration: 0, forbidClick: true });

      try {
        // 走 buildErpSessionParams 透传 ai_templateCode / force，
        // 这样"重新开始"也能保留老司机的 erp_old_driver 模板，不会退回新司机。
        const params = this.buildErpSessionParams(forceNew);
        const res = await erpDialogApi.createErpSession(params);
        Toast.clear();
        if (res.code !== 200) {
          Dialog.alert({ title: '提示', message: res.msg || '创建会话失败' });
          return;
        }
        const data = res.data;
        if (data.expireTime && Date.now() > data.expireTime) {
          Dialog.alert({ message: '会话已过期，请重新进入' });
          return;
        }
        this.sessionId = data.sessionId;
        localStorage.setItem('ai_session_id', data.sessionId);
        // 新会话，记录当前作业类型，用于下次进入时判断是否需要重新开始
        const user = this.getUserInfo();
        if (user.openid && this.jobType !== null) {
          localStorage.setItem(`ai_current_job_type_${user.openid}`, String(this.jobType));
        }
        // 新会话，清除可能残留的车牌/提货口缓存
        this.lastKnownPlanThk = '';
        this.lastKnownHeadNo = '';
        this.lastKnownTrailerNo = '';
        this.isChangingHead = false;
        localStorage.removeItem(`ai_plan_thk_${this.sessionId}`);
        localStorage.removeItem(`ai_head_no_${this.sessionId}`);
        localStorage.removeItem(`ai_trailer_no_${this.sessionId}`);
        if (data.templateCode === 'order_display' && Array.isArray(data.orderList)) {
          this.orderList = data.orderList;
          return;
        }
        this.orderList = [];
        await this.connectWebSocket(data.wsUrl);
      } catch (err) {
        Toast.clear();
        Dialog.alert({ title: '提示', message: err.message || '创建会话失败' });
      } finally {
        this.isSessionIniting = false;
      }
    },

    // ---------- WebSocket ----------
    async connectWebSocket(wsUrl) {
      if (this.isConnecting) return;
      this.isConnecting = true;
      // ERP：开发环境走 /erp-dialog 代理避免 CORS，测试/生产环境直连线上网关，不需要 token
      const isDev = process.env.NODE_ENV === 'development';
      const wsBase = isDev
        ? `ws://${window.location.host}`
        : (process.env.VUE_APP_ERP_WS_BASE || 'wss://shltyy.jsltpct.com:9526/shltyy');
      const p = wsUrl.startsWith('/') ? wsUrl : `/${wsUrl}`;
      const url = isDev ? `${wsBase}/erp-dialog${p}` : `${wsBase}${p}`;
      console.log('[WS] 连接地址', url);
      this.wsClient = new WebSocketClient(url, {
        heartbeatInterval: 30000,
        maxReconnect: 10,
        onOpen: () => {
          this.isConnecting = false;
          this.reconnectCount = 0;
        },
        onMessage: (data) => this.handleServerMessage(data),
        onClose: () => {
          this.isConnecting = false;
        },
        onError: (err) => {
          console.error('[WS] 错误', err);
          this.isConnecting = false;
        },
      });
      this.wsClient.connect();
    },
    closeWebSocket() {
      if (this.wsClient) {
        this.wsClient.close();
        this.wsClient = null;
      }
    },
    safeSend(data, showTip = true) {
      if (!this.wsClient || !this.wsClient.isOpen()) {
        if (showTip) Toast.fail('连接未建立，请稍候');
        return false;
      }
      return this.wsClient.send(data);
    },

    // ---------- 消息处理 ----------
    handleServerMessage(data) {
      if (!data || data.type === 'pong' || data.content === 'pong') return;
      if (data.type === 'history') return;
      if (data.type === 'ocr_error') {
        Toast.fail(data.content || '识别失败，请重试');
        return;
      }
      if (data.type === 'auth_error') {
        Toast.fail(data.content || '登录已过期');
        this.closeWebSocket();
        localStorage.removeItem(`ai_plan_thk_${this.sessionId}`);
        localStorage.removeItem(`ai_head_no_${this.sessionId}`);
        localStorage.removeItem(`ai_trailer_no_${this.sessionId}`);
        this.sessionId = '';
        this.sessionStatus = '';
        this.messageList = [];
        localStorage.removeItem('ai_session_id');
        return;
      }
      if (this.isBlockedMessage(data)) {
        this.handleBlockedMessage(data);
        return;
      }
      switch (data.type) {
        case 'closed':
          this.pushServerMessage(data);
          this.closeWebSocket();
          localStorage.removeItem(`ai_plan_thk_${this.sessionId}`);
          localStorage.removeItem(`ai_head_no_${this.sessionId}`);
          localStorage.removeItem(`ai_trailer_no_${this.sessionId}`);
          this.sessionId = '';
          this.sessionStatus = '';
          this.recentMsgKeys.clear();
          localStorage.removeItem('ai_session_id');
          break;
        case 'step_complete':
          this.pushServerMessage(data);
          break;
        case 'all_complete':
          this.pushServerMessage(data);
          this.sessionStatus = 'waiting_review';
          this.startReviewPolling();
          break;
        case 'review_processing':
          this.pushServerMessage({ ...data, type: 'system', role: 'system', agentName: '系统' });
          break;
        case 'review_approved':
          this.pushServerMessage(data);
          Toast.success('审核通过！');
          this.stopReviewPolling();
          this.closeWebSocket();
          localStorage.removeItem(`ai_plan_thk_${this.sessionId}`);
          localStorage.removeItem(`ai_head_no_${this.sessionId}`);
          localStorage.removeItem(`ai_trailer_no_${this.sessionId}`);
          this.sessionId = '';
          this.sessionStatus = '';
          localStorage.removeItem('ai_session_id');
          // 审核通过且后台已创建预约订单，直接跳转到订单页面，不继续停留在对话页
          this.$router.push({ name: 'orderlist' });
          break;
        case 'review_rejected':
          this.pushServerMessage(data);
          this.sessionStatus = 'bot';
          this.stopReviewPolling();
          Dialog.alert({ title: '审核未通过', message: data.rejectReason || '请重新补资料' });
          break;
        case 'step_revoke':
          if (data.revokeStep) {
            this.messageList = this.messageList.filter((m) => {
              if (m.type === 'user') return true;
              if (m.role === 'system') return true;
              return m.currentStep !== data.revokeStep;
            });
          }
          // plan_check_failed 时不再弹 Toast，直接由机器人消息提示「上传的不是车头行驶证，请重新上传」
          if (data.reason !== 'plan_check_failed') {
            Toast(data.content || '步骤已回退');
          }
          break;
        case 'expired':
          this.pushServerMessage(data);
          this.sessionStatus = 'expired';
          this.stopReviewPolling();
          break;
        case 'error':
          this.pushServerMessage(data);
          Toast.fail(data.content || '服务异常');
          break;
        default:
          this.pushServerMessage(data);
          break;
      }
      if (data.stuckCount === 1) {
        Toast('如果反复填不上，可以直接输入文字说明');
      }
    },
    isBlockedMessage(data) {
      if (!data) return false;
      if (data.type === 'block' || data.type === 'plan_check_failed') return true;
      const content = String(data.content || '');
      return /已有待作业的预约|无法重复预约|未查询到车辆计划|无法继续预约|约满|刚被约满/.test(content);
    },
    handleBlockedMessage(data) {
      if (data.type === 'plan_check_failed' && !data.content && data.message) {
        data.content = data.message;
      }
      data.isBlock = true;
      const missingTip = data.missingFields?.length ? `缺少：${data.missingFields.join('、')}` : '';
      Toast.fail(`${data.content || '操作被阻断'}${missingTip ? `（${missingTip}）` : ''}`);
      // 标记最近用户消息失败
      for (let i = this.messageList.length - 1; i >= 0; i--) {
        if (this.messageList[i].type === 'user') {
          this.$set(this.messageList[i], 'verified', false);
          break;
        }
      }
      // 撤销机器人上一步 choice/time_slot
      for (let i = this.messageList.length - 1; i >= 0; i--) {
        const m = this.messageList[i];
        if (m.type === 'user') break;
        if (m.type === 'robot' && (m.actionType === 'choice' || m.actionType === 'time_slot')) {
          this.$set(m, 'verified', false);
          break;
        }
      }
      if (data.type !== 'plan_check_failed') {
        const lastMsg = this.messageList[this.messageList.length - 1];
        if (lastMsg && lastMsg.type === 'robot' && this.isBlockedMessage({ content: lastMsg.content })) {
          lastMsg.content = data.content;
          lastMsg.rawContent = data.content;
          lastMsg.isBlock = true;
          this.scrollToBottom();
        } else {
          this.pushServerMessage(data);
        }
      }
      const blockContent = String(data.content || '');
      if (blockContent.includes('约满') && this.slotAutoRefreshCount < 2 && this.sessionId) {
        this.slotAutoRefreshCount++;
        const user = this.getUserInfo();
        this.chatApi.getHint(this.sessionId, user.openid).then((res) => {
          if (res.code === 200 && res.data) this.pushServerMessage(res.data, true);
        }).catch((err) => console.error('[AutoRefresh] 失败', err));
      }
    },
    pushServerMessage(data, skipDedup = false) {
      if (data.type === 'ocr_result') return;
      if (!skipDedup) {
        // 用户刚刚发了"上一步"时，服务端重推的提示要重新渲染，不要吞掉
        let lastUserMsgIsPrev = false;
        for (let i = this.messageList.length - 1; i >= 0; i--) {
          const um = this.messageList[i];
          if (um.type === 'user') {
            lastUserMsgIsPrev = um.content === '上一步';
            break;
          }
        }
        let existingIdx = -1;
        // 全表扫描：currentStep 兼容 + 内容相同 → 视为重复
        // - 两边 currentStep 都为空 / 一边空 / 相同 → 去重
        // - 两边都有但不同：正常"返回上一步"重推场景由 lastUserMsgIsPrev 单独处理；
        //   step_complete 会同时由 WS 和 HTTP botReply 双通道推送，内容相同即合并，避免重复渲染
        for (let i = this.messageList.length - 1; i >= 0; i--) {
          const m = this.messageList[i];
          if (m.type === 'user') continue;
          // 兜底：step_complete + 同 currentStep 直接合并（防止 HTTP/WS 双通道因内容细微差异漏掉去重）
          if (data.type === 'step_complete' && (m.serverType === 'step_complete' || m.currentStep === data.currentStep) && data.currentStep && m.currentStep === data.currentStep) {
            existingIdx = i;
            break;
          }
          if (m.content !== data.content && m.rawContent !== data.content) continue;
          const dataStep = data.currentStep || '';
          const mStep = m.currentStep || '';
          const sameStep = !dataStep || !mStep || dataStep === mStep;
          // step_complete 由 WS 与 HTTP botReply 双通道推送，内容相同即合并，避免重复渲染
          if (sameStep || data.type === 'step_complete' || m.serverType === 'step_complete') {
            existingIdx = i;
            break;
          }
        }
        // "已经是第一步，无法返回上一步" 每次都要显示，避免被去重吞掉
        if (existingIdx !== -1 && String(this.messageList[existingIdx].content || '').includes('已经是第一步')) {
          existingIdx = -1;
        }
        // 用户刚发完"上一步" → 跳过 content 去重，让服务端 botReply 重新 push 到底部
        if (existingIdx !== -1 && lastUserMsgIsPrev) {
          existingIdx = -1;
        }
        if (existingIdx !== -1) {
          // 关键：直接改 existing 的属性，不要替换成新对象！
          // 否则 fetchPlansForMessage(msg) 闭包里持有的旧 msg 引用会指向 detached 对象，
          // API 返回后只更新了 detached 的旧 msg，UI 看不到 plans。
          const existing = this.messageList[existingIdx];
          this.$set(existing, 'serverType', data.type);
          this.$set(existing, 'time', this.formatTime(new Date()));
          if (data.stepType === 'choice' && data.options?.length) {
            // 如果现有选项已经被前端注入了"确认车头/车挂"专用按钮（带 continueBlue/lightRed 标记），
            // 不要被后端带车牌号的原始 options 覆盖掉，否则按钮又会显示成"继续使用该车挂（...）"。
            const existingHasVehicleConfirm = Array.isArray(existing.options) && existing.options.some((o) => o && (o.continueBlue || o.lightRed));
            if (!existingHasVehicleConfirm) {
              this.$set(existing, 'actionType', 'choice');
              this.$set(existing, 'options', data.options);
            }
            // 用户返回上一步后，新的确认车头/车挂消息会 dedup 命中旧消息。
            // 如果旧消息不在末尾，要移到末尾，否则用户看不到当前步骤的按钮。
            const isConfirmVehicleMove = Array.isArray(existing.options) && existing.options.some((o) => o && (o.continueBlue || o.lightRed));
            if (isConfirmVehicleMove && existingIdx !== this.messageList.length - 1) {
              this.messageList.splice(existingIdx, 1);
              this.messageList.push(existing);
              existingIdx = this.messageList.length - 1;
            }
          }
          // 关键：ERP 流程下 time_slot 需要前端再调 getpdxxnew 拿时间段，
          // 但 WS 先推了一条纯 text/step_complete 的同内容消息占位、HTTP 后推 step_complete botReply 时被 dedup 命中，
          // 这里要主动补一次 fetchTimeSlotsForMessage，否则 API 永远不会被触发。
          const isTimeSlot = data.stepType !== 'summary' && (data.stepType === 'time_slot' || data.currentStep === 'choose_time' || data.stepName === 'choose_time');
          if (isTimeSlot) {
            this.$set(existing, 'actionType', 'time_slot');
            // 用户返回上一步重选计划/日期后，新的 choose_time botReply 会 dedup 命中旧消息。
            // 如果旧消息不在末尾，直接更新会导致时间段选择卡出现在历史位置，不在当前流末尾。
            // 这里把它移到末尾，并清掉旧选项重新拉取，确保时间段选择始终渲染在最后。
            if (existingIdx !== this.messageList.length - 1) {
              this.messageList.splice(existingIdx, 1);
              this.messageList.push(existing);
              existingIdx = this.messageList.length - 1;
            }
            if (existing.options && existing.options.length) {
              this.$set(existing, 'options', []);
              this.$set(existing, 'slotsRequested', false);
            }
            if ((!existing.options || !existing.options.length) && !existing.slotsRequested) {
              this.$set(existing, 'loadingSlots', true);
              this.fetchTimeSlotsForMessage(existing);
            }
          }
          if (data.stepType === 'date_picker') this.$set(existing, 'actionType', 'date_picker');
          if (data.stepType === 'summary') {
            this.$set(existing, 'actionType', 'choice');
            // 信息确认步骤要强制替换掉之前可能残留的时间段选项，避免再次触发 getpdxxnew
            this.$set(existing, 'options', [{
              text: '确认信息',
              value: '0',
              step: data.currentStep || 'confirm',
              type: 'primary',
            }]);
            this.$set(existing, 'loadingSlots', false);
            // 返回上一步重选后，旧 summary 可能还在历史消息里，要移到末尾才会显示在最新用户消息下方
            if (existingIdx !== this.messageList.length - 1) {
              this.messageList.splice(existingIdx, 1);
              this.messageList.push(existing);
              existingIdx = this.messageList.length - 1;
            }
          }
          // choose_plan 需要前端再调 getJhByChe 拉计划列表。
          // 注意：第八步 choose_time 的 botReply 里 stepName='choose_plan'（已提交步骤），
          // 所以 isChoosePlan 也会被满足；但 time_slot 步骤已经在上面处理了，
          // 这里要互斥：只有当这一帧不是 time_slot 时，才走 fetchPlansForMessage，
          // 否则两个 fetch 会竞速、慢的那个会覆盖 msg.options，导致第八步气泡里渲染成计划卡片。
          const isChoosePlan = data.currentStep === 'choose_plan' || data.stepName === 'choose_plan';
          if (isChoosePlan && !isTimeSlot
            && (!existing.options || !existing.options.length) && !existing.plansRequested) {
            this.$set(existing, 'loadingPlans', true);
            this.fetchPlansForMessage(existing);
          }
          this.scrollToMessage(existingIdx);
          return;
        }
      }
      let role = 'robot';
      let name = '智能助手';
      const serverType = data.type;
      if (serverType === 'error' || serverType === 'expired' || serverType === 'welcome') {
        role = 'system';
        name = '系统';
      }
      const msg = {
        type: 'robot',
        role,
        content: data.content,
        rawContent: data.content,
        currentStep: data.currentStep,
        serverType,
        agentName: name,
        ossUrl: data.ossUrl,
        images: data.ossUrl ? [data.ossUrl] : undefined,
        time: this.formatTime(new Date()),
        exampleImage: this.detectExampleImage(data.content),
        isBlock: data.isBlock || false,
      };
      if (data.stepType === 'date_picker') msg.actionType = 'date_picker';
      if (data.stepType !== 'summary' && (data.stepType === 'time_slot' || data.currentStep === 'choose_time' || data.stepName === 'choose_time')) {
        msg.actionType = 'time_slot';
        console.log('[pushServerMessage] 进入 time_slot 分支 msg.options=', msg.options);
        if (msg.content) {
          msg.content = msg.content.split(/\r?\n/).filter((line) => !/^\s*\d+\./.test(line)).join('\n');
          const firstLineEnd = msg.content.indexOf('\n');
          const firstLine = firstLineEnd === -1 ? msg.content : msg.content.slice(0, firstLineEnd);
          const restAfterFirst = firstLineEnd === -1 ? '' : msg.content.slice(firstLineEnd);
          const titleHint = firstLine.match(/^【([^】]+)】\s*(.+)$/);
          if (titleHint) {
            msg.content = `【${titleHint[1]}】\n${titleHint[2]}${restAfterFirst}`;
          }
        }
        // ERP 流程下 choose_time 需要前端再调一次 getpdxxnew 拿时间段
        if (!msg.options || !msg.options.length) {
          msg.loadingSlots = true;
          this.fetchTimeSlotsForMessage(msg);
        }
      }
      if (data.stepType === 'choice') {
        const isChoosePlan = data.currentStep === 'choose_plan' || data.stepName === 'choose_plan';
        // 【老司机】确认车头/车挂步骤：后端 stepType='choice' 但不传 options，前端按步骤注入按钮
        const confirmMap = {
          confirm_head: { continueText: '继续使用', changeText: '更换车头' },
          confirm_trailer: { continueText: '继续使用', changeText: '更换车挂' },
        };
        const confirmStep = confirmMap[data.currentStep]
          ? data.currentStep
          : (confirmMap[data.stepName] ? data.stepName : '');
        // 兜底：后端没给 currentStep/stepName 时，按消息内容识别确认车头/车挂
        const confirmStepByContent = !confirmStep
          ? (/【确认车头】/.test(data.content) ? 'confirm_head' : (/【确认车挂】/.test(data.content) ? 'confirm_trailer' : ''))
          : '';
        const finalConfirmStep = confirmStep || confirmStepByContent;
        if (confirmStepByContent) {
          console.log('[pushServerMessage] 按内容识别到确认车辆步骤:', finalConfirmStep, 'currentStep=', data.currentStep, 'stepName=', data.stepName);
        }
        const confirmCfg = confirmMap[finalConfirmStep];
        const isConfirmVehicle = !!confirmCfg;
        if (isChoosePlan) {
          // choose_plan：后端只给 stepType，不给 options，需要前端调 getJhByChe 拉计划列表
          msg.actionType = 'choice';
          msg.options = data.options || [];
          msg.loadingPlans = !msg.options.length;
          if (msg.loadingPlans) this.fetchPlansForMessage(msg);
        } else if (isConfirmVehicle) {
          // 按钮只显示 4 个字「继续使用/更换车头」，完整文本（车牌号）通过 /submit 时再拼到 value 提交，
          //   用户消息气泡再渲染成 `继续使用该车头（皖KZ1968）`（plateHighlight 把括号一并染红）。
          const plateMatch = String(data.content || '').match(
            /[京津沪渝冀豫云辽黑湘皖鲁新苏浙赣鄂桂甘晋蒙陕吉闽贵粤青藏川宁琼][A-Z][A-Z0-9]{3,6}[挂学警港澳]?/
          );
          const plateNo = plateMatch ? plateMatch[0] : '';
          const item = finalConfirmStep === 'confirm_head' ? '车头' : '车挂';
          msg.actionType = 'choice';
          msg.options = [
            {
              text: confirmCfg.continueText,
              value: plateNo ? `继续使用该${item}（${plateNo}）` : confirmCfg.continueText,
              type: 'primary',
              step: finalConfirmStep,
              continueBlue: true,
            },
            {
              text: confirmCfg.changeText,
              value: plateNo ? `更换该${item}（${plateNo}）` : confirmCfg.changeText,
              type: 'danger',
              step: finalConfirmStep,
              lightRed: true,
            },
          ];
          console.log('[pushServerMessage] 注入确认车辆按钮:', finalConfirmStep, 'options=', msg.options.map(o => o.text));
        } else if (data.options?.length) {
          msg.actionType = 'choice';
          msg.options = data.options;
        }
        if (msg.actionType === 'choice' && msg.content) {
          msg.content = msg.content.split(/\r?\n/).filter((line) => !/^\s*\d+\./.test(line)).join('\n');
          // 兼容后端不补 \n 的情况：把"【标题】提示"强制拆成两行，与 DatePickerMessage 保持一致
          const firstLineEnd = msg.content.indexOf('\n');
          const firstLine = firstLineEnd === -1 ? msg.content : msg.content.slice(0, firstLineEnd);
          const restAfterFirst = firstLineEnd === -1 ? '' : msg.content.slice(firstLineEnd);
          const titleHint = firstLine.match(/^【([^】]+)】\s*(.+)$/);
          if (titleHint) {
            msg.content = `【${titleHint[1]}】\n${titleHint[2]}${restAfterFirst}`;
          }
        }
      }
      if (data.stepType === 'summary') {
        // 信息确认步骤：后端 stepType='summary' 没有自带按钮，前端补一个蓝色"确认信息"按钮，
        // 点击后提交 /submit(confirm, value='0')。
        msg.actionType = 'choice';
        if (!msg.options || !msg.options.length) {
          msg.options = [{
            text: '确认信息',
            value: '0',
            step: data.currentStep || 'confirm',
            type: 'primary',
          }];
        }
      }
      if (data.quickReplies?.length) msg.quickReplies = data.quickReplies;
      if (data.buttons?.length) msg.buttons = data.buttons;
      const progressMatch = msg.content?.match(/\[进度：[^\]]+\]/);
      if (progressMatch && msg.content) {
        msg.progress = progressMatch[0].slice(1, -1);
        msg.content = msg.content.replace(progressMatch[0], '').trim();
      }
      this.messageList.push(msg);
      // 异步换票：把 ossUrl / exampleImage 中落到本桶（ruoyicould）的 URL 换成 10 分钟有效的签名 URL。
      // 保留原 URL 作为初始值（占位），签名 URL 拿到后通过 $set 触发响应式刷新；
      // ImageMessage 里的 watch.msg.images 会把 imgLoadFailed 重置为 false，"图片加载失败"占位会自动隐藏。
      if (data.ossUrl || msg.exampleImage) {
        const tasks = [];
        if (data.ossUrl) {
          tasks.push(this.resolveImageUrl(data.ossUrl).then((signed) => {
            if (signed && signed !== data.ossUrl) this.$set(msg, 'images', [signed]);
          }));
        }
        if (msg.exampleImage) {
          const origExample = msg.exampleImage;
          tasks.push(this.resolveImageUrl(origExample).then((signed) => {
            // exampleImage 是字符串，不是数组，直接赋值即可
            if (signed && signed !== origExample) msg.exampleImage = signed;
          }));
        }
        if (tasks.length) Promise.all(tasks).catch(() => {});
      }
      this.isSubmitting = false;
      this.scrollToBottom();
      if (data.stepType === 'time_slot') {
        this.slotAutoRefreshCount = 0;
      }
    },
    getMessageComponent(msg) {
      if (msg.actionType === 'choice') return 'ChoiceMessage';
      if (msg.actionType === 'date_picker') return 'DatePickerMessage';
      if (msg.actionType === 'time_slot') return 'TimeSlotMessage';
      if (msg.images && msg.images.length) return 'ImageMessage';
      return 'TextMessage';
    },

    // ---------- 用户发送 ----------
    checkActionCooldown() {
      const now = Date.now();
      if (now - this.lastActionTime < 1200) {
        Toast('操作太快，请稍后再试');
        return false;
      }
      this.lastActionTime = now;
      return true;
    },
    containsKeyword(text, keywords) {
      return keywords.some((kw) => text.includes(kw));
    },
    getCurrentMandatoryAction() {
      const lastMsg = this.messageList[this.messageList.length - 1];
      if (!lastMsg || lastMsg.type !== 'robot') return null;
      if (lastMsg.actionType === 'date_picker' && !lastMsg.verified) return 'date_picker';
      if (lastMsg.actionType === 'time_slot' && !lastMsg.verified) return 'time_slot';
      return null;
    },
    sendText() {
      if (!this.checkActionCooldown()) return;
      const content = this.inputValue.trim();
      if (!content) return;
      if (this.isInputDisabled) {
        Toast.fail(this.getStatusTip());
        return;
      }
      if (this.containsKeyword(content, GO_BACK_KEYWORDS)) {
        this.inputValue = '';
        this.goBackErpAndRender();
        return;
      }
      const robotMsgIndex = this.messageList.length - 1;
      const mandatoryAction = this.getCurrentMandatoryAction();
      this.sendToServer(content);
      if (mandatoryAction && !this.promptedMsgIndices.has(robotMsgIndex)) {
        this.promptedMsgIndices.add(robotMsgIndex);
        Dialog.alert({ message: mandatoryAction === 'date_picker' ? '请点击选择预约日期' : '请点击选择时间段' });
      }
      this.inputValue = '';
    },
    sendToServer(content) {
      const traceId = `msg_${Date.now()}`;
      const msg = {
        clientMsgId: traceId,
        traceId,
        msgType: 'text',
        content,
      };
      // ERP 接口通过 WebSocket 路径识别会话，不需要 sessionId
      if (!this.safeSend(msg)) return;
      this.messageList.push({ type: 'user', content, time: this.formatTime(new Date()), verified: true });
      this.scrollToBottom();
    },

    // ---------- 交互处理 ----------
    handleChoice(choice) {
      if (!this.checkActionCooldown()) return;
      const text = typeof choice === 'string' ? choice : choice.text;
      const value = typeof choice === 'string' ? choice : choice.value;
      if (text === '提货' || value === '提货' || value === 'PICKUP') this.jobType = 0;
      if (text === '卸货' || value === '卸货' || value === 'DELIVERY') this.jobType = 1;
      localStorage.setItem('ai_job_type', this.jobType);
      const lastIdx = this.messageList.length - 1;
      if (lastIdx >= 0 && this.messageList[lastIdx].actionType === 'choice') {
        this.$set(this.messageList[lastIdx], 'verified', true);
      }
      // 通用 ERP 步骤选择：选项带 step 字段（如老司机 confirm_head 的继续使用/更换车头）
      // 或带 kind='plan'（choose_plan 步骤）→ 都走 submitErpStepAndRender
      const erpStep = choice && (choice.step || (choice.kind === 'plan' ? 'choose_plan' : null));
      if (erpStep) {
        // 记住选中计划的提货扣（仅 plan 需要，choose_time 步骤 getpdxxnew 会用）
        if (choice.kind === 'plan') {
          this.lastKnownPlanThk = (choice && choice.thk) || '';
          // 按 sessionId 落盘 localStorage：新司机选择计划后可能退出，再进时
          // 后端只记录了计划单号，没有提货口，需要从本地恢复。
          if (this.sessionId && this.lastKnownPlanThk) {
            localStorage.setItem(`ai_plan_thk_${this.sessionId}`, this.lastKnownPlanThk);
          }
        }
        // 【P-老司机】confirm_head/confirm_trailer：按 erp-frontend-api(3).html §6.3，
        //   /submit 期望 value 为 '0'（继续用）/ '1'（换车），不是长文本。
        //   用户消息气泡仍用完整 value 渲染（plateHighlight 把车牌号 part 染红加粗带括号）。
        if (erpStep === 'confirm_head' || erpStep === 'confirm_trailer') {
          if (choice.continueBlue) {
            // 继续使用：先调 /judgePhWh 查计划（参考新司机确认车头流程），
            //   有计划才 /submit(value='0')；handleConfirmVehicleContinue 内部会自己渲染用户消息气泡。
            this.handleConfirmVehicleContinue(erpStep, value || text);
            return;
          }
          // 更换车头/车挂：直接 /submit(value='1')，用户消息气泡用完整 value（带车牌号）
          if (erpStep === 'confirm_head') {
            // 老司机更换车头：后续按"新司机"流程重新收集车头/车挂，旧的本地缓存先清掉，
            // 避免副页交叉校验、查计划时用到上一次来过的旧车牌。
            this.isChangingHead = true;
            this.lastKnownHeadNo = '';
            this.lastKnownTrailerNo = '';
            const user = this.getUserInfo();
            if (this.sessionId) {
              localStorage.removeItem(`ai_head_no_${this.sessionId}`);
              localStorage.removeItem(`ai_trailer_no_${this.sessionId}`);
            }
            if (user.openid) {
              localStorage.removeItem(`ai_head_no_${user.openid}`);
              localStorage.removeItem(`ai_trailer_no_${user.openid}`);
              localStorage.setItem(`ai_changing_head_${user.openid}`, '1');
            }
            console.log('[handleChoice 更换车头] isChangingHead=true, 已清除旧车头/车挂缓存');
          } else if (erpStep === 'confirm_trailer') {
            // 老司机更换车挂：只清车挂，车头保留
            this.lastKnownTrailerNo = '';
            const user = this.getUserInfo();
            if (this.sessionId) {
              localStorage.removeItem(`ai_trailer_no_${this.sessionId}`);
            }
            if (user.openid) {
              localStorage.removeItem(`ai_trailer_no_${user.openid}`);
            }
          }
          this.messageList.push({
            type: 'user',
            content: value || text,
            time: this.formatTime(new Date()),
            verified: true,
          });
          this.scrollToBottom();
          this.submitErpStepAndRender(erpStep, '1');
          return;
        }
        // 其它 erpStep（choose_plan 等）：保持原逻辑
        this.messageList.push({
          type: 'user',
          content: text,
          time: this.formatTime(new Date()),
          verified: true,
        });
        this.scrollToBottom();
        this.submitErpStepAndRender(erpStep, value || text);
        return;
      }
      this.sendToServer(text);
    },
    // 【P-老司机】继续使用车头/车挂：先调 /judgePhWh 查计划，有计划才走 /submit。
    // 老司机进入 erp_old_driver 模板就一定是普货（普货/危货在首页已分流），这里只判断"是否有计划"。
    //   - 有计划 → 调 /submit 推进
    //   - 无计划 → 推"未查询到车辆计划信息" block 消息，不调 /submit
    async handleConfirmVehicleContinue(step, value) {
      // 从 value 里提取车牌号，例如 "继续使用该车头（皖KZ1968）"
      const plateMatch = String(value || '').match(
        /[京津沪渝冀豫云辽黑湘皖鲁新苏浙赣鄂桂甘晋蒙陕吉闽贵粤青藏川宁琼][A-Z][A-Z0-9]{3,6}/
      );
      const plateNo = plateMatch ? plateMatch[0] : '';
      if (!plateNo) {
        Toast.fail('未识别到车牌号，请重试');
        return;
      }
      // 渲染用户消息：(尾号) 拼在 "继续使用" 后面渲染，TextMessage 的 plateHighlight mixin
      // 会自动把里面的车牌号 part 渲染成红色加粗 18px（与 ChoiceMessage 气泡里样式一致）。
      const item = step === 'confirm_head' ? '车头' : '车挂';
      const userMsg = {
        type: 'user',
        content: `继续使用该${item}（${plateNo}）`,
        time: this.formatTime(new Date()),
        verified: true,
      };
      this.messageList.push(userMsg);
      this.scrollToBottom();
      // 构造 queryErpPlans 需要的 sourceStep（车头→vehicle_license_front，车挂→trailer_license_front）
      const sourceStep = step === 'confirm_head' ? 'vehicle_license_front' : 'trailer_license_front';
      // 同步保存到会话变量（与 onPlateConfirm 行为一致，下游 /submit 推进后端要读）
      if (step === 'confirm_head') {
        this.lastKnownHeadNo = plateNo.replace(/挂$/, '');
      } else {
        const clean = plateNo.replace(/挂$/, '');
        this.lastKnownTrailerNo = clean.endsWith('挂') ? clean : (clean + '挂');
      }
      Toast.loading({ message: '查询计划中...', duration: 0, forbidClick: true });
      let planInfo = null;
      try {
        // confirm_head 只查车头，不拿内存/历史里的车挂号兜底，避免车挂号被提前带到 /judgePhWh
        planInfo = await this.queryErpPlans(sourceStep, { plateNo }, { onlyPrimary: step === 'confirm_head' });
      } catch (err) {
        Toast.clear();
        console.error('[handleConfirmVehicleContinue] queryErpPlans 失败', err);
        Toast.fail('查询计划失败');
        this.$set(userMsg, 'verified', false);
        return;
      }
      Toast.clear();
      // 无计划 → 先判是否油脂车辆（进错对话），是则引导去丽天车队小程序；否则推 block 消息，不调 /submit
      if (planInfo === null || !planInfo.headPlan || !planInfo.headPlan.hasPlan) {
        this.$set(userMsg, 'verified', false);
        if (step === 'confirm_head' && await this.checkOilVehicleByPlate(plateNo)) {
          this.showOilGuide();
          return;
        }
        this.pushServerMessage({
          type: 'robot',
          role: 'robot',
          content: '未查询到车辆计划信息，请联系客服进行车辆计划创建，无法继续预约。',
          isBlock: true,
        }, true);
        return;
      }
      // 有计划 → 调 /submit 推进（按文档 /submit 期望 value='0' 表示继续用）
      this.submitErpStepAndRender(step, '0');
    },
    handleFetchDates(index) {
      const msg = this.messageList[index];
      if (!msg) return;
      this.$set(msg, 'loadingDates', true);
      api.getkyyyrq({})
        .then((resData) => {
          const res = JSON.parse(resData);
          const code = String(res.respCode || '');
          if (code !== '0' || !Array.isArray(res.data)) {
            Toast.fail(res.message || '获取可预约日期失败');
            return;
          }
          const dates = res.data.map((item) => item.pdrq).filter(Boolean);
          if (!dates.length) {
            Dialog.alert({ message: '未查询到可用预约日期' });
            return;
          }
          this.$set(msg, 'availableDates', dates);
          // 计算日历可选范围：最早到最晚
          const first = new Date(dates[0].replace(/-/g, '/'));
          const last = new Date(dates[dates.length - 1].replace(/-/g, '/'));
          this.minDate = first;
          this.maxDate = last;
          this.calendarMsgIndex = index;
          this.calendarShow = true;
        })
        .catch((err) => {
          console.error('[getkyyyrq] 失败', err);
          Toast.fail('获取可预约日期失败');
        })
        .finally(() => {
          this.$set(msg, 'loadingDates', false);
        });
    },
    onCalendarConfirm(date) {
      this.calendarShow = false;
      const msg = this.calendarMsgIndex !== null ? this.messageList[this.calendarMsgIndex] : null;
      const dateStr = this.formatDateStr(date);
      // 兜底校验：必须在 availableDates 里
      if (msg && msg.availableDates && msg.availableDates.length && !msg.availableDates.includes(dateStr)) {
        Toast.fail('该日期不在可选范围内，请重新选择');
        return;
      }
      if (msg) this.$set(msg, 'verified', true);
      this.selectedDate = dateStr;
      // 把用户选择的日期以对话消息形式渲染到页面
      this.messageList.push({
        type: 'user',
        content: `预约日期：${dateStr}`,
        time: this.formatTime(new Date()),
        verified: true,
      });
      this.scrollToBottom();
      // 调 /submit(choose_date)
      this.submitErpStepAndRender('choose_date', dateStr);
    },
    handleSlotConfirm(slotOrPayload) {
      if (!this.checkActionCooldown()) return;
      // 兼容三种入参：
      // 1) TimeSlotMessage 老版本：直接传 slot 对象
      // 2) TimeSlotMessage 新版本（@slot-confirm）：传 { slot, text }
      // 3) 字符串：兼容老 API
      const slot = slotOrPayload && slotOrPayload.slot !== undefined ? slotOrPayload.slot : slotOrPayload;
      const providedText = slotOrPayload && slotOrPayload.text;
      const text = providedText || (typeof slot === 'object' && slot !== null ? slot.text : slot) || '';
      // ERP 分支：调 /submit(choose_time)，value 用选中的时段 id（与老 appointmentxhIndexNew 一致）
      const value = typeof slot === 'object' && slot !== null ? slot.value : slot;
      Dialog.confirm({
        title: '确认时段',
        message: `您选择的是 ${text}，确认吗？`,
        confirmButtonText: '确认',
        cancelButtonText: '取消',
      }).then(() => {
        const lastIdx = this.messageList.length - 1;
        if (lastIdx >= 0 && this.messageList[lastIdx].actionType === 'time_slot') {
          this.$set(this.messageList[lastIdx], 'verified', true);
          this.$set(this.messageList[lastIdx], 'selectedSlotValue', String(value));
        }
        // 把用户选择的时段以对话消息形式渲染到页面
        this.messageList.push({
          type: 'user',
          content: `预约时段：${text}`,
          time: this.formatTime(new Date()),
          verified: true,
        });
        this.scrollToBottom();
        // 调 /submit(choose_time)，value 用 "时间段|id" 拼接（例如 "06:15-06:30|4566f0..."）
        this.submitErpStepAndRender('choose_time', `${text}|${value}`);
      }).catch(() => {});
    },
    // ERP 选择类步骤提交（§8）：/session/erp/submit
    async submitErpStepAndRender(step, value) {
      const user = this.getUserInfo();
      if (!user.openid || !this.sessionId) {
        Toast.fail('缺少会话信息');
        return;
      }
      Toast.loading({ message: '提交中...', duration: 0, forbidClick: true });
      try {
        const resp = await erpDialogApi.submitErpStep({
          openid: user.openid,
          sessionId: this.sessionId,
          step,
          value,
        });
        Toast.clear();
        if (resp.code !== 200 || !resp.data) {
          Toast.fail(resp.msg || '提交失败');
          return;
        }
        const botReply = resp.data.botReply;
        if (botReply) {
          // 注意：不传 skipDedup=true，与 uploadAndOCR 保持一致。
          // 时序场景：WS step_complete 可能比 HTTP 响应先到，
          // 若 skipDedup=true 会绕过 dedup，导致同 currentStep 的消息在列表里渲染两次。
          // "返回上一步"重推场景由 lastUserMsgIsPrev 兜底逻辑处理。
          this.pushServerMessage({
            ...botReply,
            currentStep: botReply.currentStep || resp.data.currentStep,
          });
        }
      } catch (err) {
        Toast.clear();
        console.error('[submitErpStep] 失败', err);
        Toast.fail(err.message || '网络异常，请稍后重试');
      }
    },

    // ---------- 图片上传与 OCR ----------
    triggerImageUpload() {
      if (!this.checkActionCooldown()) return;
      this.$nextTick(() => {
        const input = this.$refs.fileInputRef;
        if (input) input.click();
      });
    },
    onFileChange(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.compressAndUpload(file);
      event.target.value = '';
    },
    compressAndUpload(file) {
      if (!file) return;
      Toast.loading({ message: '图片处理中...', duration: 0, forbidClick: true });
      console.log('[debug compressAndUpload] file=', file && {name: file.name, type: file.type, size: file.size});
      let handled = false;
      const onSuccess = (result) => {
        if (handled) return;
        handled = true;
        console.log('[debug compressAndUpload] success, result type=', result && result.type);
        Toast.clear();
        const compressedFile = new File([result], file.name || 'image.jpg', { type: result.type });
        this.uploadAndOCR(compressedFile);
      };
      const onError = (err) => {
        if (handled) return;
        handled = true;
        console.error('[debug compressAndUpload] error', err);
        Toast.clear();
        // 压缩失败时直接走原文件上传，避免卡住
        this.uploadAndOCR(file);
      };
      try {
        new Compressor(file, {
          quality: 0.8,
          maxWidth: 1920,
          success: onSuccess,
          error: onError,
        });
        // 兜底：8 秒内若 Compressor 没回调（卡死场景），走原文件
        setTimeout(() => {
          if (!handled) {
            console.warn('[debug compressAndUpload] timeout, fallback to original file');
            Toast.clear();
            this.uploadAndOCR(file);
            handled = true;
          }
        }, 8000);
      } catch (e) {
        onError(e);
      }
    },
    async uploadAndOCR(file) {
      const localUrl = URL.createObjectURL(file);
      const tempMsg = { type: 'user', content: '', images: [localUrl], uploading: true, time: this.formatTime(new Date()) };
      this.messageList.push(tempMsg);
      this.scrollToBottom();
      // sourceStep 提到 try 之前声明，让 catch 块也能拿到（用于业务错误友好提示）
      let sourceStep = null;
      try {
        // ERP 上传与 OCR 流程（参考 erp-process-ocr-frontend-api.md v2）
        const user = this.getUserInfo();
        if (!user.openid) throw new Error('缺少 openid');
        if (!this.sessionId) throw new Error('缺少 sessionId');

        // 【P5】upload 前从后端拿真实 currentStep 作为 sourceStep。
        //   修复 detectErpSourceStep 读 messageList 不可靠的 bug：
        //   当 planInfo=null 时 processErpOcr 不推进，messageList 末尾仍是旧 botReply，
        //   导致 detect 拿到旧 sourceStep 与 DB 真实 currentStep 不一致。
        //   getSession 是后端 DB 的权威值，优先用它（ERP 用 erpDialogApi，无需 JWT）。
        try {
          const sessResp = await erpDialogApi.getSession(this.sessionId, user.openid);
          if (sessResp && sessResp.code === 200 && sessResp.data && sessResp.data.currentStep) {
            this.sessionCurrentStep = sessResp.data.currentStep;
            console.log('[uploadAndOCR] 从后端拿 sessionCurrentStep=', this.sessionCurrentStep);
          }
        } catch (e) {
          console.warn('[uploadAndOCR] getSession 失败，fallback 到 detectErpSourceStep', e);
        }

        // 【P6】优先用后端真实 currentStep；如果拿不到再 fallback 到 messageList 推断
        if (this.sessionCurrentStep && this.normalizeErpSourceStep(this.sessionCurrentStep)) {
          sourceStep = this.normalizeErpSourceStep(this.sessionCurrentStep);
        } else {
          sourceStep = this.detectErpSourceStep();
        }
        const cardSide = this.detectErpCardSide(sourceStep);

        // 在询问是否继续使用车头/车挂时，禁止直接上传图片，引导用户先点按钮
        if (sourceStep === 'confirm_head' || sourceStep === 'confirm_trailer') {
          const tip = sourceStep === 'confirm_head'
            ? '请点击继续使用或者更换车头按钮'
            : '请点击继续使用或者更换车挂按钮';
          // 移除刚插入的临时上传消息，避免界面上留下"上传中"占位
          const tempIdx = this.messageList.indexOf(tempMsg);
          if (tempIdx !== -1) this.messageList.splice(tempIdx, 1);
          URL.revokeObjectURL(localUrl);
          Dialog.alert({ title: '提示', message: tip });
          return;
        }

        // Step 1：保存图片到 OSS（保留给审核用）。
        //   不传 ocrResult，让后端根据 sourceStep 路由 OCR 并兜底（runOcrHeadVehicleLicense / runOcrTrailerLicense）。
        //   后端 validatePlateNoMatchesStep 还会兜底校验 plateNo 与 sourceStep 类别一致性。
        const uploadResp = await erpDialogApi.uploadErpImage({
          file,
          openid: user.openid,
          sessionId: this.sessionId,
          sourceStep,
          cardSide,
        });
        // 业务校验错误（如"图片类型不匹配"）走聊天提示，不弹 Toast
        if (uploadResp && uploadResp.code !== undefined && uploadResp.code !== 200) {
          const errMsg = uploadResp.msg || `上传失败 code=${uploadResp.code}`;
          if (this.isBusinessImageError(errMsg)) {
            this.$set(tempMsg, 'uploading', false);
            this.$set(tempMsg, 'verified', 'failed');
            this.pushServerMessage({
              type: 'robot',
              role: 'robot',
              content: this.getBusinessErrorTip(errMsg, sourceStep),
              isBlock: true,
            }, true);
            return;
          }
          throw new Error(errMsg);
        }
        const respData = uploadResp.data || uploadResp;
        const ossUrl = respData.ossUrl;
        // ★ 直接用后端 OCR 结果，不再调前端 OCR（ERP 前端没有 JWT，调 /shltyy/gyyz/ocr/* 会 401）
        // 后端 uploadImage 返回的 ocrResult 可能是 JSON 字符串，需要先反序列化，
        // 否则 ocrData.plateNo / cardCode 等字段都是 undefined，导致 lastKnownHeadNo 等
        // 车牌号始终为空，下游 fetchPlansForMessage 拿不到车牌号。
        let ocrData = respData.ocrResult;
        if (typeof ocrData === 'string') {
          try {
            ocrData = JSON.parse(ocrData);
          } catch (e) {
            console.warn('[OCR] ocrResult 解析失败', e, ocrData);
            ocrData = {};
          }
        }
        if (!ocrData || typeof ocrData !== 'object') ocrData = {};
        if (ossUrl) {
          // 【绕过私有桶授权】用户刚上传的图：保留本地 blob URL 给前端展示，
          // 不再替换成 OSS 永久 URL，避免为“当前展示”调 presign 换票。
          // ossUrl 仍落到 tempMsg.ossUrl，便于刷新后从历史恢复或调试时使用。
          this.$set(tempMsg, 'ossUrl', ossUrl);
          // 注意：流程推进、后端 OCR 只认 ocrData，不需要 images 是 ossUrl；
          // 只有“从历史消息恢复/机器人消息图片”才需要后续换票。
        }

        // 后端 OCR 失败提示
        if (ocrData.success === false) {
          this.$set(tempMsg, 'uploading', false);
          this.$set(tempMsg, 'verified', 'failed');
          Toast.fail(ocrData.message || '识别失败，请重新上传清晰的证件图片');
          return;
        }
        this.$set(tempMsg, 'uploading', false);
        this.$set(tempMsg, 'verified', true);

        // 【副本反页】只上传、不校验，上传成功后需再调 processErpOcr 才能真正推进
        if (sourceStep === 'vehicle_license_copy' || sourceStep === 'trailer_license_copy') {
          await this.processErpOcrForCopy(sourceStep, respData);
          return;
        }

        // Step 2：OCR 类别校验（§2.2.2，靠 plateNo.endsWith('挂') 区分车头/车挂）
        const validation = this.validateErpOcr(sourceStep, ocrData);
        if (!validation.ok) {
          this.$set(tempMsg, 'verified', 'failed');
          this.pushServerMessage({
            type: 'robot',
            role: 'robot',
            content: validation.tip,
          }, true);
          return;
        }

        // Step 3：副页（back）必须与正页（front）识别到的车牌一致，防止上传别的证件也通过。
        //   用户可能中途退出再进，正页值不在内存时从历史接口（messages / slotsJson）兜底拿。
        if (sourceStep === 'vehicle_license_back' || sourceStep === 'trailer_license_back') {
          const backValidation = await this.validateErpOcrBackPage(sourceStep, ocrData);
          if (!backValidation.ok) {
            this.$set(tempMsg, 'verified', 'failed');
            this.pushServerMessage({
              type: 'robot',
              role: 'robot',
              content: backValidation.tip,
              isBlock: true,
            }, true);
            return;
          }
        }

        // Step 4：车牌号/车挂号确认（车头/车挂行驶证正页必须弹）
        //   即使 OCR 没识别到也要弹，让司机手动输入。
        //   OCR 识别有错时司机也能在弹框里改 — 弹框"确定"之后的值才是权威值。
        if (sourceStep === 'vehicle_license_front' || sourceStep === 'trailer_license_front') {
          const ocrPlateNo = String(ocrData.plateNo || '');
          this.pendingErpOcr = { ocrData, sourceStep, ossUrl, plateNo: ocrPlateNo };
          this.pendingTempMsg = tempMsg;
          this.confirmPlateNo = ocrPlateNo;
          this.showPlateConfirm = true;
          return;
        }

        // Step 5：查计划（正页、副页需要校验；驾驶证不需要校验但 planInfo 要占位）
        //   queryErpPlans 对 driver_license_front 会直接返回空 planInfo，不会调 judgePhWh。
        //   副页（back）也要调 judgePhWh 验证车牌是否有计划，没计划说明上传的不是对应车辆的副页。
        const planInfo = await this.queryErpPlans(sourceStep, ocrData);
        if (sourceStep === 'vehicle_license_back' || sourceStep === 'trailer_license_back') {
          if (planInfo === null || !planInfo.headPlan || !planInfo.headPlan.hasPlan) {
            this.$set(tempMsg, 'verified', 'failed');
            const labelMap = {
              vehicle_license_back: '车头行驶证副本正面',
              trailer_license_back: '车挂行驶证副本正面',
            };
            this.pushServerMessage({
              type: 'robot',
              role: 'robot',
              content: `上传的不是${labelMap[sourceStep]}，请重新上传`,
              isBlock: true,
            }, true);
            return;
          }
        } else if (sourceStep !== 'driver_license_front') {
          // 【权威字段优先】phWhDriver === 'WEIHUO_NEW' → 危货新用户 → 跳出对话去 addAllCar 绑定
          if (planInfo && planInfo.phWhDriver === 'WEIHUO_NEW') {
            this.$set(tempMsg, 'verified', 'failed');
            // 新司机 AI 对话危货绑定流程开始，先重置标记，待 addAllCar 绑定完成后再置为 true
            this.$store.commit('set_aiChatVehicleBound', false);
            Dialog.alert({
              title: '提示',
              message: '系统检测到您是危货新用户，请先绑定车辆信息',
              confirmButtonText: '去绑定',
            }).then(() => {
              this.$router.push({ name: 'addAllCar', query: { source: 'aichat' } });
            });
            return;
          }
          if (planInfo === null || !planInfo.headPlan || !planInfo.headPlan.hasPlan) {
            // 无计划（包含 judgePhWh 调用失败 / plans 为空两种情况）
            this.$set(tempMsg, 'verified', 'failed');
            this.pushServerMessage({
              type: 'robot',
              role: 'robot',
              content: '未查询到车辆计划信息，请联系客服进行车辆计划创建，无法继续预约。',
              isBlock: true,
            }, true);
            return;
          }
          if (planInfo.isPuhuo === false) {
            // 查到计划但是含危货 → 弹框 → 跳转绑定车辆页
            this.$set(tempMsg, 'verified', 'failed');
            // 新司机 AI 对话危货绑定流程开始，先重置标记，待 addAllCar 绑定完成后再置为 true
            this.$store.commit('set_aiChatVehicleBound', false);
            Dialog.alert({
              title: '提示',
              message: '系统检测到该车辆是危货，请先绑定车辆信息',
              confirmButtonText: '去绑定',
            }).then(() => {
              this.$router.push({ name: 'addAllCar', query: { source: 'aichat' } });
            });
            return;
          }
        }

        // Step 6：调用 processErpOcr 推进步骤
        const resp = await erpDialogApi.processErpOcr({
          openid: user.openid,
          sessionId: this.sessionId,
          sourceStep,
          ocrResult: ocrData,
          planInfo,
        });
        if (resp.code !== 200 || !resp.data) {
          throw new Error(resp.msg || `processErpOcr 失败 code=${resp.code}`);
        }
        // 渲染下一步 botReply
        const botReply = resp.data.botReply;
        if (botReply) {
          this.pushServerMessage({
            ...botReply,
            currentStep: botReply.currentStep || resp.data.currentStep,
          });
          // 注意：不传 skipDedup=true，让全表 dedup 兜底。
          // 时序场景：WS step_complete 可能比 HTTP 响应先到，若 skipDedup=true 会绕过 dedup 重复 push。
          // "返回上一步"重推场景由 lastUserMsgIsPrev 兜底逻辑处理。
        }
      } catch (err) {
        Toast.clear();
        this.$set(tempMsg, 'uploading', false);
        this.$set(tempMsg, 'verified', false);
        // 业务校验错误（如"图片类型不匹配"）走聊天提示，不弹 Toast
        if (this.isBusinessImageError(err.message)) {
          this.pushServerMessage({
            type: 'robot',
            role: 'robot',
            content: this.getBusinessErrorTip(err.message, sourceStep),
            isBlock: true,
          }, true);
        } else {
          Toast.fail(err.message || '上传失败');
        }
      }
    },
    // ERP 前端 OCR 类别校验（§2.2.2）
    validateErpOcr(sourceStep, ocrData) {
      const plateNo = String(ocrData.plateNo || '');
      const cardCode = String(ocrData.cardCode || '');
      const page = String(ocrData.page || '');
      const labelMap = {
        vehicle_license_front: '车头行驶证正页',
        vehicle_license_back: '车头行驶证副页',
        vehicle_license_copy: '车头行驶证副本反页',
        trailer_license_front: '车挂行驶证正页',
        trailer_license_back: '车挂行驶证副页',
        trailer_license_copy: '车挂行驶证副本反页',
        driver_license_front: '驾驶证正页',
      };
      const label = labelMap[sourceStep] || '证件';
      if (sourceStep === 'vehicle_license_front' || sourceStep === 'vehicle_license_back') {
        if (plateNo && plateNo.endsWith('挂')) {
          return {
            ok: false,
            tip: `当前步骤是【${label}】，但识别到车牌 ${plateNo}（车挂），请上传正确的车头行驶证${sourceStep === 'vehicle_license_front' ? '正页' : '副页'}`,
          };
        }
        return { ok: true };
      }
      if (sourceStep === 'trailer_license_front' || sourceStep === 'trailer_license_back') {
        if (!plateNo || !plateNo.endsWith('挂')) {
          return {
            ok: false,
            tip: `当前步骤是【${label}】，但识别到${plateNo ? `车牌 ${plateNo}（车头）` : '非车挂车牌'}，请上传正确的车挂行驶证${sourceStep === 'trailer_license_front' ? '正页' : '副页'}`,
          };
        }
        return { ok: true };
      }
      if (sourceStep === 'driver_license_front') {
        const name = String(ocrData.name || '').trim();
        if (!cardCode.trim() || !name) {
          return { ok: false, tip: '上传的不是驾驶证正页，请重新上传。' };
        }
        return { ok: true };
      }
      return { ok: true };
    },
    // 行驶证副页与正页交叉校验：
    //   上传 vehicle_license_back / trailer_license_back 时，必须和正页（front）识别到的车牌一致。
    //   用户可能中途退出再进，正页值不在内存时从历史接口（messages / slotsJson）兜底拿。
    //   若历史接口也找不到正页记录，则跳过校验（避免阻塞异常场景），仅记日志。
    async validateErpOcrBackPage(sourceStep, ocrData) {
      const isHeadBack = sourceStep === 'vehicle_license_back';
      const isTrailerBack = sourceStep === 'trailer_license_back';
      if (!isHeadBack && !isTrailerBack) return { ok: true };

      const mainKey = isHeadBack ? 'lastKnownHeadNo' : 'lastKnownTrailerNo';
      const label = isHeadBack ? '车头' : '车挂';
      const mainLabel = isHeadBack ? '车头行驶证正页' : '车挂行驶证正页';
      const backLabel = isHeadBack ? '车头行驶证副页' : '车挂行驶证副页';

      let mainPlate = this[mainKey];
      if (!mainPlate) {
        await this.ensurePlatesFromHistory();
        mainPlate = this[mainKey];
      }

      const backPlate = String(ocrData.plateNo || '');
      const normalize = (p) => String(p).replace(/挂$/, '').toUpperCase();

      if (!mainPlate) {
        console.warn(`[validateErpOcrBackPage] 未找到${mainLabel}历史记录，跳过交叉校验`);
        return { ok: true };
      }
      if (!backPlate) {
        return {
          ok: false,
          tip: `当前上传的是${backLabel}，但未识别到车牌号，请重新上传清晰的证件图片`,
        };
      }
      if (normalize(mainPlate) !== normalize(backPlate)) {
        return {
          ok: false,
          tip: `当前上传的是${backLabel}，识别到车牌 ${backPlate}，与${mainLabel}识别到的 ${mainPlate} 不一致，请上传与正页对应的${label}行驶证副页`,
        };
      }
      return { ok: true };
    },
    // 判定是否为"图片类型不匹配"/"图片识别失败"/"sourceStep 与当前步骤不一致"等业务校验错误
    // 命中后应推送到聊天窗口而不是弹 Toast
    isBusinessImageError(msg) {
      if (!msg) return false;
      const s = String(msg);
      return /图片类型不匹配|图片识别失败|sourceStep\s*与当前步骤不一致|上传的不是车|识别到车挂车牌|识别到车头车牌|与实际上传的卡证正副页类型不符|请上传正确的车头行驶证|请上传正确的车挂行驶证|请上传清晰的证件图片后重试|不一致|请上传与正页对应/.test(s);
    },
    // 业务错误 → 友好提示文案
    //   - "sourceStep 与当前步骤不一致" 这种技术性提示，根据当前 sourceStep 翻译成"上传的不是xx，请重新上传"
    //   - "图片类型不匹配：" / "图片识别失败：" 透传时去掉前缀
    getBusinessErrorTip(msg, sourceStep) {
      if (!msg) return msg;
      const s = String(msg);
      const labelMap = {
        vehicle_license_front: '车头行驶证正页',
        vehicle_license_back: '车头行驶证副页',
        vehicle_license_copy: '车头行驶证副本反页',
        trailer_license_front: '车挂行驶证正页',
        trailer_license_back: '车挂行驶证副页',
        trailer_license_copy: '车挂行驶证副本反页',
        driver_license_front: '驾驶证正页',
      };
      if (/sourceStep\s*与当前步骤不一致/.test(s)) {
        const label = labelMap[sourceStep] || '当前证件';
        return `上传的不是${label}，请重新上传`;
      }
      return this.stripBusinessPrefix(s);
    },
    // 去掉业务错误前缀（"图片类型不匹配：" / "图片识别失败："），让聊天里显示更干净
    stripBusinessPrefix(msg) {
      if (!msg) return msg;
      return String(msg).replace(/^(图片类型不匹配|图片识别失败)[：:]\s*/, '');
    },
    // ERP 查计划（用 /judgePhWh 一次性返回普危 + plans，按 dhyy-api(3).md §3）
    // 入参：openid + 车头号（必填） + 车挂号（如果已 OCR 出）
    // 返回：{ headPlan, trailerPlan, isPuhuo, type, phWhDriver }
    //   - headPlan.hasPlan=false → 无计划
    //   - isPuhuo=false → 含危货计划，需去绑定页
    //   - phWhDriver='WEIHUO_NEW' → 权威字段：新用户危货，需跳 addAllCar 走车辆绑定
    // opts.onlyPrimary: 老司机 confirm_head 时只传车头号，不拿内存/历史里的车挂号兜底，
    //   避免用户还没点"继续车挂"就把车挂号带到 /judgePhWh。
    async queryErpPlans(sourceStep, ocrData, opts = {}) {
      // 驾驶证类不需要查计划
      if (sourceStep === 'driver_license_front') {
        return { headPlan: null, trailerPlan: null, isPuhuo: true, phWhDriver: '' };
      }
      const user = this.getUserInfo();
      if (!user.openid) return null;
      const txhlb = this.jobType === 1 ? '卸货' : '提货';

      // 【P-新司机断点续约】本地 lastKnownHeadNo / lastKnownTrailerNo 缺失时，
      //   从后端历史 messages 里回填上次 OCR 的车牌号。
      //   场景：用户中途退出再进，前端组件 state 丢了（Vue data 没了），
      //   但后端会话的 OCR 结果还在 messages 里，兜底补回本地。
      await this.ensurePlatesFromHistory();

      // 【优化】按 sourceStep 判断 chetouhao / cheguahao 谁是主参数：
      //   - 车挂步骤（trailer_license_*）：ocrData.plateNo 是车挂号，chetouhao 优先用本会话已 OCR 出的车头
      //   - 车头步骤（vehicle_license_*）：ocrData.plateNo 是车头号，cheguahao 优先用本会话已 OCR 出的车挂
      //   这样 vehicle_license_back（已 OCR 过 trailer）能查得全；trailer_license_back 也能查得全。
      // onlyPrimary=true 时：只查当前主参数，不兜底补另一个车牌。
      // 车头相关步骤（vehicle_license_*）永远只查车头：新司机本来就没车挂，老司机"更换车头"时
      // 也不能把旧车挂号带进来；车挂相关步骤（trailer_license_*）才需要车头+车挂一起查。
      const ocrPlateNo = String(ocrData.plateNo || '');
      const onlyPrimary = (opts && opts.onlyPrimary) || sourceStep.startsWith('vehicle_license_');
      let chetouhao = '';
      let cheguahao = '';
      if (sourceStep.startsWith('trailer_license_')) {
        cheguahao = ocrPlateNo;
        if (!onlyPrimary) {
          chetouhao = String(this.lastKnownHeadNo || '');
        }
      } else {
        chetouhao = ocrPlateNo;
        if (!onlyPrimary) {
          cheguahao = String(this.lastKnownTrailerNo || '');
        }
      }

      // 兜底：如果当前步骤的 plateNo 拿不到，但会话已经 OCR 过其它 plateNo，复用它
      if (!onlyPrimary) {
        if (!chetouhao && this.lastKnownHeadNo) chetouhao = String(this.lastKnownHeadNo);
        if (!cheguahao && this.lastKnownTrailerNo) cheguahao = String(this.lastKnownTrailerNo);
      }

      // judgePhWh 要求车挂号/车头号均去掉末尾的"挂"字
      if (chetouhao && chetouhao.endsWith('挂')) {
        chetouhao = chetouhao.replace(/挂$/, '');
      }
      if (cheguahao && cheguahao.endsWith('挂')) {
        cheguahao = cheguahao.replace(/挂$/, '');
      }
      console.log('[queryErpPlans] sourceStep=', sourceStep, 'isChangingHead=', this.isChangingHead, 'onlyPrimary=', onlyPrimary, 'chetouhao=', chetouhao, 'cheguahao=', cheguahao);

      try {
        const respStr = await api.judgePhWh(user.openid, txhlb, chetouhao, cheguahao);
        const resp = JSON.parse(respStr);
        if (!resp.ok || !resp.data) {
          console.error('[ERP judgePhWh] 返回失败', resp);
          return null;
        }
        const data = resp.data;
        const plans = Array.isArray(data.plans) ? data.plans : [];
        const hasPlan = plans.length > 0;
        // plans 中任一项 isPuhuo=false 即视为含危货
        const isPuhuo = !hasPlan || plans.every((p) => p.isPuhuo !== false);
        // 权威字段 phWhDriver：PUHUO_NEW / PUHUO_OLD / WEIHUO_NEW / WEIHUO_OLD / UNKNOWN
        const phWhDriver = data.phWhDriver ? String(data.phWhDriver) : '';
        return {
          headPlan: { hasPlan, planCount: plans.length, plans },
          trailerPlan: { hasPlan, planCount: plans.length, plans },
          isPuhuo,
          type: data.type,
          phWhDriver,
        };
      } catch (err) {
        console.error('[ERP judgePhWh] 失败', err);
        // 网络异常时按"无计划"处理，避免推进后被后端阻断
        return null;
      }
    },

    // 【纠偏-油脂引导】按车头号直查丽天车队（油脂/小程序）计划：
    // 老 ERP(ltxt) 档案与油脂系统数据不互通，油脂车在老系统查不到；
    // 作业类型与仓储首页一致：点"提货装车"→ai_job_type=0→查提货计划(car_raise_plans)，
    // 点"预约卸车"→1→查卸货计划(car_unload_plans)，未选择时两个都查兜底。
    // 命中任一有货(loadCount>0 / count>0)即说明司机进错对话，应去丽天车队小程序预约。
    // 任何异常返回 false（维持原"未查询到计划"提示，不阻断、不误导）。
    async checkOilVehicleByPlate(plateNo) {
      try {
        const user = this.getUserInfo();
        const clean = String(plateNo || '').replace(/挂$/, '');
        if (!user.openid || !clean) return false;

        // 响应结构兼容：纯数组 或 { total, rows, code, msg } 对象（后端实测返回后者）
        const extractRows = (res) => {
          if (Array.isArray(res)) return res;
          if (res && Array.isArray(res.rows)) return res.rows;
          return [];
        };

        const isUnload = this.jobType === 1;
        const isPickup = this.jobType === 0;
        let rows = [];
        if (isUnload || !isPickup) {
          // 卸货 或 未知（两个都查）：先查卸货
          const unloadData = await api.checkOilUnloadPlan(user.openid, clean).catch(() => null);
          const unloadRes = unloadData ? (typeof unloadData === 'string' ? JSON.parse(unloadData) : unloadData) : null;
          const unloadRows = extractRows(unloadRes);
          if (unloadRows.some((r) => Number(r && r.count) > 0)) {
            console.log('[checkOilVehicleByPlate] plate=', clean, 'jobType=', this.jobType, '命中卸货计划');
            return true;
          }
          rows = rows.concat(unloadRows);
        }
        if (isPickup || !isUnload) {
          const raiseData = await api.checkOilRaisePlan(user.openid, clean).catch(() => null);
          const raiseRes = raiseData ? (typeof raiseData === 'string' ? JSON.parse(raiseData) : raiseData) : null;
          const raiseRows = extractRows(raiseRes);
          if (raiseRows.some((r) => Number(r && r.loadCount) > 0)) {
            console.log('[checkOilVehicleByPlate] plate=', clean, 'jobType=', this.jobType, '命中提货计划');
            return true;
          }
          rows = rows.concat(raiseRows);
        }
        console.log('[checkOilVehicleByPlate] plate=', clean, 'jobType=', this.jobType, 'rows=', rows.length, '未命中油脂计划');
        return false;
      } catch (e) {
        console.warn('[checkOilVehicleByPlate] 失败', e);
        return false;
      }
    },
    // 【纠偏-油脂引导】推送引导消息并弹出「去丽天车队预约」弹窗（含跳小程序开放标签按钮）
    showOilGuide() {
      this.pushServerMessage({
        type: 'robot',
        role: 'robot',
        content: '系统检测到您的车辆属于丽天车队业务，请前往小程序完成预约。',
      }, true);
      this.oilGuideWxState = 'loading';
      this.oilGuideWxError = '';
      this.showOilGuideDialog = true;
    },
    // 弹窗打开动画完成后初始化微信配置并挂载开放标签按钮
    onOilGuideDialogOpened() {
      ensureWxConfig().then(() => {
        this.oilGuideWxState = 'ready';
        this.$nextTick(() => {
          mountLaunchButton('oil-guide-btn-box', {
            // 直接落到小程序 AI 对话（油脂预约）页；不要带 entry=steamAppointment / showHome=0，
            // 否则首页会强制停留在首页，不进入对话页
            path: 'subPages/aiCustomerService/aiCustomerService',
            text: '去丽天车队预约',
          });
        });
      }).catch((err) => {
        this.oilGuideWxState = 'error';
        this.oilGuideWxError = (err && err.message) || '微信配置失败，请稍后重试';
      });
    },

    // ERP 上传后，继续推送后端 botReply 推进对话
    continueErpFlow(data) {
      const nextBotReply = data.botReply;
      if (nextBotReply) {
        this.pushServerMessage(nextBotReply, true);
      }
    },
    // 【副本反页/反页】不需要 OCR 识别，上传成功后直接调 /erp/processOcr 推进步骤
    async processErpOcrForCopy(sourceStep, uploadRespData) {
      const user = this.getUserInfo();
      if (!user.openid || !this.sessionId) {
        throw new Error('缺少会话信息');
      }
      // 副本步骤不查计划、不 OCR，传空 planInfo + 标记 ocrSkipped
      const planInfo = {
        headPlan: { hasPlan: true, planCount: 0, plans: [] },
        trailerPlan: { hasPlan: true, planCount: 0, plans: [] },
      };
      const resp = await erpDialogApi.processErpOcr({
        openid: user.openid,
        sessionId: this.sessionId,
        sourceStep,
        ocrResult: { success: true, ocrSkipped: true },
        planInfo,
      });
      if (resp.code !== 200 || !resp.data) {
        // 若 processOcr 未兼容副本步骤，兜底使用 uploadImage 返回的 botReply
        const fallbackBotReply = uploadRespData && uploadRespData.botReply;
        if (fallbackBotReply) {
          console.warn('[processErpOcrForCopy] processOcr 失败，fallback 到 uploadImage botReply', resp.msg);
          this.pushServerMessage({
            ...fallbackBotReply,
            currentStep: fallbackBotReply.currentStep || uploadRespData.currentStep,
          });
          return;
        }
        throw new Error(resp.msg || `processErpOcr 失败 code=${resp.code}`);
      }
      const botReply = resp.data.botReply;
      if (botReply) {
        this.pushServerMessage({
          ...botReply,
          currentStep: botReply.currentStep || resp.data.currentStep,
        });
      }
    },
    // 【P-新/老司机断点续约】本地 lastKnownHeadNo / lastKnownTrailerNo 缺失时，
    //   从两个数据源兜底：
    //     1) /session/{sessionId}/messages 历史 confirm_head / confirm_trailer 文本消息（仅老司机）
    //        例：【确认车头】...皖KZ1968... / 继续使用该车头（皖KZ1968）
    //     2) /session/{sessionId} 的 slotsJson 里的 truck_head_no / truck_trailer_no 字段
    //        （这是 fillTimeSlotParamsFromHistory 已经在用的权威字段，正页 processErpOcr 成功后
    //          后端会把车牌号落到 slotsJson 里）
    //   注意：不直接用历史 OCR 消息回填车头/车挂正页车牌，因为历史里可能包含失败重传记录，
    //        会导致副页交叉校验被污染。
    // 调用时机：queryErpPlans（judgePhWh）、fetchPlansForMessage（getJhByChe）两个查计划入口都调。
    // 与 fillPlateNoFromHistory 的区别：
    //   - fillPlateNoFromHistory 只用于 choose_plan 步骤兜底，会调 Toast 提示；
    //   - 本方法静默兜底，不弹 Toast，失败也不阻断流程（后续兜底逻辑继续走）。
    async ensurePlatesFromHistory() {
      // 都齐了就不用拉
      if (this.lastKnownHeadNo && this.lastKnownTrailerNo) return;
      const user = this.getUserInfo();
      if (!user.openid || !this.sessionId) return;
      try {
        const res = await erpDialogApi.getMessages(this.sessionId, user.openid, 100);
        if (res.code === 200 && Array.isArray(res.data)) {
          const plateRegex = /[京津沪渝冀豫云辽黑湘皖鲁新苏浙赣鄂桂甘晋蒙陕吉闽贵粤青藏川宁琼][A-Z][A-Z0-9]{3,6}[挂学警港澳]?/g;
          (res.data || []).forEach((m) => {
            if (!m) return;

            // 来源 1：老司机 confirm_head / confirm_trailer 消息（bot 或 user 文本）
            //   示例内容：
            //     【确认车头】您上次的车辆号是 皖KZ1968，是否继续使用该车头？
            //     继续使用该车头（皖KZ1968）
            //     更换该车头（皖KZ1968）
            //     【确认车挂】您上次的车辆号是 皖KRW63，是否继续使用该车挂？
            //     继续使用该车挂（皖KRW63）
            const content = String(m.content || '');
            if (!content) return;
            // 跳过模板占位符消息（{truck_head_no} 没替换的版本），不要让"占位符"误当成车号
            if (/\{[^}]{1,}\}/.test(content) && !/[京津沪渝冀豫云辽黑湘皖鲁新苏浙赣鄂桂甘晋蒙陕吉闽贵粤青藏川宁琼][A-Z][A-Z0-9]{3,6}/.test(content)) {
              return;
            }
            const isHeadContext = /【确认车头】|继续使用该车头|更换该车头/.test(content);
            const isTrailerContext = /【确认车挂】|继续使用该车挂|更换该车挂/.test(content);
            if (!isHeadContext && !isTrailerContext) return;
            plateRegex.lastIndex = 0;
            const matches = content.match(plateRegex);
            if (!matches || !matches.length) return;
            const plate = matches[0];
            if (isHeadContext && !this.lastKnownHeadNo) {
              this.lastKnownHeadNo = plate.replace(/挂$/, '');
            } else if (isTrailerContext && !this.lastKnownTrailerNo) {
              const clean = plate.replace(/挂$/, '');
              this.lastKnownTrailerNo = clean.endsWith('挂') ? clean : (clean + '挂');
            }
          });
        }
        // 来源 2：slotsJson.truck_head_no / truck_trailer_no（权威字段）
        //   老司机"继续使用"提交后，后端会把 confirm_head / confirm_trailer 的车牌号落到 slotsJson。
        //   即使 history messages 是占位符版本（{truck_head_no}），slotsJson 也可能已经有真实车号。
        if ((!this.lastKnownHeadNo || !this.lastKnownTrailerNo)) {
          try {
            const sess = await erpDialogApi.getSession(this.sessionId, user.openid);
            if (sess && sess.code === 200 && sess.data) {
              let slots = {};
              if (sess.data.slotsJson) {
                try {
                  slots = typeof sess.data.slotsJson === 'string'
                    ? JSON.parse(sess.data.slotsJson)
                    : sess.data.slotsJson;
                } catch (e) { slots = {}; }
              }
              if (!this.lastKnownHeadNo && slots.truck_head_no) {
                this.lastKnownHeadNo = String(slots.truck_head_no).replace(/挂$/, '');
              }
              if (!this.lastKnownTrailerNo && slots.truck_trailer_no) {
                const clean = String(slots.truck_trailer_no).replace(/挂$/, '');
                this.lastKnownTrailerNo = clean.endsWith('挂') ? clean : (clean + '挂');
              }
            }
          } catch (e) {
            console.warn('[ensurePlatesFromHistory] getSession 失败', e);
          }
        }
        console.log('[ensurePlatesFromHistory] 恢复 lastKnownHeadNo=', this.lastKnownHeadNo, 'lastKnownTrailerNo=', this.lastKnownTrailerNo);
      } catch (err) {
        console.warn('[ensurePlatesFromHistory] 失败', err);
      }
    },

    // choose_plan 步骤：调 getJhByChe 拉作业计划列表
    async fetchPlansForMessage(msg) {
      // 防重入：dedup 失效、HTTP/WS 双 push 等极端情况下，确保只调一次
      if (msg.plansRequested) return;
      this.$set(msg, 'plansRequested', true);

      const txhlb = this.jobType === 1 ? '卸货' : '提货';

      // 【P-老/新司机断点续约】本地 lastKnown* 缺失 → 从历史 messages 补回（OCR 或老司机 confirm 消息）
      //   老司机"继续使用"流程不产生 OCR 消息，但 confirm_head/trailer 消息文本里有车牌号，
      //   ensurePlatesFromHistory 会识别它们。改 await 是因为该方法内部 await 了 getMessages。
      await this.ensurePlatesFromHistory();

      const localHead = String(this.lastKnownHeadNo || '');
      const localTrailer = String(this.lastKnownTrailerNo || '');

      // 本地有就直接调
      if (localHead || localTrailer) {
        const chetouhao = localHead;
        const cheguahao = localTrailer.replace(/挂$/, '');
        this.callGetJhByChe(msg, chetouhao, cheguahao, txhlb);
        return;
      }

      // 本地为空（OCR 没识别到）→ 兜底拉历史 ocr_result
      this.fillPlateNoFromHistory(msg, txhlb);
    },

    // 实际发 getJhByChe 请求 + 处理响应（抽出来复用）
    callGetJhByChe(msg, chetouhao, cheguahao, txhlb) {
      console.log('[fetchPlansForMessage] 调用 getJhByChe:', { chetouhao, cheguahao, txhlb });
      api.getJhByChe(chetouhao, cheguahao, txhlb)
        .then((resData) => {
          let res = resData;
          if (typeof res === 'string') res = JSON.parse(res);
          console.log('[fetchPlansForMessage] getJhByChe 返回:', res);
          // 兼容三种响应格式：
          //   A) { respCode: "0", data: {...} }  老格式
          //   B) { code: 200, message: "...", data: {...} }  新格式
          //   C) 干脆没有包装，body 直接是 { plans: [...], ... }
          let plans = [];
          if (res && Array.isArray(res.plans)) {
            plans = res.plans;
          } else if (res && res.data && Array.isArray(res.data.plans)) {
            plans = res.data.plans;
          } else if (res && res.data && Array.isArray(res.data)) {
            plans = res.data;
          }
          const codeRaw = res && (res.respCode !== undefined ? res.respCode : res.code);
          const code = String(codeRaw === undefined ? '' : codeRaw);
          const ok = code === '0' || code === '200' || (res && res.ok === true) || plans.length > 0;
          console.log('[fetchPlansForMessage] 解析 code=', code, 'plans.length=', plans.length, 'ok=', ok);
          if (!ok) {
            Toast.fail((res && res.message) || '获取作业计划失败');
            this.$set(msg, 'loadingPlans', false);
            this.$set(msg, 'plansRequested', false);
            return;
          }
          if (!plans.length) {
            Toast.fail('未查询到可用作业计划');
            this.$set(msg, 'loadingPlans', false);
            this.$set(msg, 'plansRequested', false);
            return;
          }
          // 把后端 fields 渲染成结构化的"计划卡片"选项，label 蓝色 / 值默认色
          //   单号: jhdh
          //   送货单位: songhuodanwei
          //   品名: pinzhong
          //   客户通知单号: khdh
          //   有效日期: youxiaoriqi (YYYY-MM-DD HH:mm)
          //   重量: zhongliangTxt
          // 同时把 tkh 一起带上，方便 handleChoice 记住给 getpdxxnew 用
          // 兼容老选项（字符串 / {text, value}），非计划消息走 fallback
          const options = plans.map((plan) => ({
            kind: 'plan',
            jhdh: String(plan.jhdh || ''),
            songhuodanwei: String(plan.songhuodanwei || ''),
            pinzhong: String(plan.pinzhong || ''),
            khdh: String(plan.khdh || ''),
            youxiaoriqi: String(plan.youxiaoriqi || '').substring(0, 16),
            zhongliangTxt: String(plan.zhongliangTxt || ''),
            value: String(plan.jhdh || ''),
            // 兼容后端字段名 tkh / thk
            thk: String(plan.thk || plan.tkh || ''),
            // 兼容老逻辑：仍拼一份 text（如果有非计划消息组件 fallback 用）
            text: [
              `单号:${plan.jhdh || ''}`,
              `送货单位:${plan.songhuodanwei || ''}`,
              `品名:${plan.pinzhong || ''}`,
              `客户通知单号:${plan.khdh || ''}`,
              `有效日期:${String(plan.youxiaoriqi || '').substring(0, 16)}`,
              `重量:${plan.zhongliangTxt || ''}`,
            ].join('\n'),
          })).filter((o) => o.value);
          this.$set(msg, 'options', options);
          this.$set(msg, 'loadingPlans', false);
          // 内容渲染完后自动滚到底部，让用户看到完整的计划卡片
          this.$nextTick(() => this.scrollToBottom());
        })
        .catch((err) => {
          console.error('[getJhByChe] 失败', err);
          Toast.fail('获取作业计划失败');
          this.$set(msg, 'loadingPlans', false);
        });
    },

    // 从后端历史消息（GET /dialog/session/erp/{sessionId}/messages）里把
    // vehicle_license_front / trailer_license_front 的 plateNo 提出来，
    // 把 plateNo 填到 lastKnownHeadNo / lastKnownTrailerNo，然后回调 getJhByChe。
    // 适用场景：用户中途退出再进，前端组件 state 丢了，但后端会话里 OCR 结果还在。
    fillPlateNoFromHistory(msg, txhlb) {
      const user = this.getUserInfo();
      if (!user.openid || !this.sessionId) {
        Toast.fail('缺少会话信息，无法获取车牌号');
        return;
      }
      erpDialogApi.getMessages(this.sessionId, user.openid, 100)
        .then((res) => {
          if (res.code !== 200 || !Array.isArray(res.data)) {
            Toast.fail(res.message || '获取历史车牌号失败');
            return;
          }
          let headNo = '';
          let trailerNo = '';
          // 历史消息里有两种形式都带 OCR 结果：
          //   1) WS ocr_result 类型（type === 'ocr_result'）
          //   2) 用户上传图片消息（msgType === 'image'，内嵌 ocrResult 字段）
          // 下面两种都识别，按 sourceStep 优先取正页（front）
          (res.data || []).forEach((m) => {
            if (!m) return;
            const isOcrMessage = m.type === 'ocr_result' || m.msgType === 'ocr_result' || (m.msgType === 'image' && m.ocrResult);
            if (!isOcrMessage) return;
            const sourceStep = String(m.sourceStep || '');
            if (!sourceStep) return;
            let ocr = m.ocrResult;
            if (typeof ocr === 'string') {
              try { ocr = JSON.parse(ocr); } catch (e) { ocr = null; }
            }
            const plateNo = ocr && ocr.plateNo ? String(ocr.plateNo) : '';
            if (!plateNo) return;
            if (sourceStep === 'vehicle_license_front' && !headNo) {
              headNo = plateNo.replace(/挂$/, '');
            } else if (sourceStep === 'trailer_license_front' && !trailerNo) {
              const clean = plateNo.replace(/挂$/, '');
              trailerNo = clean.endsWith('挂') ? clean : (clean + '挂');
            }
          });
          console.log('[fillPlateNoFromHistory] headNo=', headNo, 'trailerNo=', trailerNo);
          if (!headNo && !trailerNo) {
            Toast.fail('历史记录中未找到车牌号，请返回上一步重新上传行驶证');
            return;
          }
          // 缓存到本地
          if (headNo) this.lastKnownHeadNo = headNo;
          if (trailerNo) this.lastKnownTrailerNo = trailerNo;
          // 用历史里拿到的车牌号真正调 getJhByChe
          this.callGetJhByChe(msg, headNo, (trailerNo || '').replace(/挂$/, ''), txhlb);
        })
        .catch((err) => {
          console.error('[fillPlateNoFromHistory] 失败', err);
          Toast.fail('获取历史车牌号失败');
        });
    },

    // 从后端历史接口（优先 /dialog/session/erp/{sessionId}/messages）把
    // 用户在第 6 步选的预约日期（yyrq）和第 7 步选的作业计划（jhdh）捞出来，
    // 再走 getJhByChe 反查该计划的提货扣（thk），最后回调 getpdxxnew 拉时间段。
    // 适用场景：新司机中途退出再进，前端组件 state 丢了，需要从历史消息恢复参数。
    // 兜底：messages 里找不到时，再用 getSession + slotsJson 恢复。
    // 优化：如果本地已经保存了 thk（如选择计划时落盘 localStorage），则直接用它，
    //   不再走 getJhByChe 反查。
    async fillTimeSlotParamsFromHistory(msg) {
      const user = this.getUserInfo();
      if (!user.openid || !this.sessionId) {
        Toast.fail('缺少会话信息，无法获取预约参数');
        this.$set(msg, 'loadingSlots', false);
        this.$set(msg, 'slotsRequested', false);
        return;
      }

      let finalDate = String(this.selectedDate || '');
      let planThk = String(this.lastKnownPlanThk || '');
      let planJhdh = '';

      // 1. 缺日期或缺提货口时，优先从历史 messages 接口解析用户已经发送过的参数
      if (!finalDate || !planThk) {
        try {
          const res = await erpDialogApi.getMessages(this.sessionId, user.openid, 100);
          if (res.code === 200 && Array.isArray(res.data)) {
            const messages = [...res.data].reverse();
            for (const m of messages) {
              if (!m) continue;
              const isUser = m.type === 'user' || m.senderType === 'user' || m.role === 'user';
              if (!isUser) continue;
              const content = String(m.content || '');

              if (!finalDate) {
                const dateMatch = content.match(/预约日期[：:]\s*(\d{4}-\d{2}-\d{2})/);
                if (dateMatch) finalDate = dateMatch[1];
              }

              if (!planThk && !planJhdh) {
                const planMatch = content.match(/单号[：:]\s*([^\n]+)/);
                if (planMatch) planJhdh = planMatch[1].trim();
              }

              if (!planThk && !planJhdh && m.value) {
                const v = String(m.value);
                if (v && v !== content) planJhdh = v;
              }
            }
          }
        } catch (e) {
          console.warn('[fillTimeSlotParamsFromHistory] getMessages 失败', e);
        }
      }
      console.log('[fillTimeSlotParamsFromHistory] 从历史消息解析 date=', finalDate, 'jhdh=', planJhdh, 'thk=', planThk);

      // 2. messages 里找不到日期/计划时，兜底读 session.slotsJson
      if (!finalDate || (!planThk && !planJhdh)) {
        try {
          const res = await erpDialogApi.getSession(this.sessionId, user.openid);
          if (res && res.code === 200 && res.data) {
            let slots = {};
            const raw = res.data.slotsJson;
            if (raw) {
              try {
                slots = typeof raw === 'string' ? JSON.parse(raw) : raw;
              } catch (e) {
                console.error('[fillTimeSlotParamsFromHistory] parse slotsJson 失败', e, raw);
              }
            }
            if (!finalDate) finalDate = String(slots.choose_date || '');
            if (!planThk && !planJhdh) planJhdh = String(slots.choose_plan || '');
            // 补充车牌号到内存
            const localHead = String(this.lastKnownHeadNo || '');
            const localTrailer = String(this.lastKnownTrailerNo || '');
            if (!localHead && slots.truck_head_no) {
              this.lastKnownHeadNo = String(slots.truck_head_no).replace(/挂$/, '');
            }
            if (!localTrailer && slots.truck_trailer_no) {
              const clean = String(slots.truck_trailer_no).replace(/挂$/, '');
              this.lastKnownTrailerNo = clean.endsWith('挂') ? clean : (clean + '挂');
            }
          }
        } catch (e) {
          console.warn('[fillTimeSlotParamsFromHistory] getSession 兜底失败', e);
        }
      }

      // 日期：messages 和 slotsJson 都没有 → 必须提示用户重选
      if (!finalDate) {
        Toast.fail('历史记录中未找到预约日期，请返回上一步重新选择日期');
        this.$set(msg, 'loadingSlots', false);
        this.$set(msg, 'slotsRequested', false);
        return;
      }

      // 3. 已经有提货口了，直接调 getpdxxnew
      if (planThk) {
        this.selectedDate = finalDate;
        this.callGetPdxxNew(msg, planThk, finalDate);
        return;
      }

      // 4. 没有提货口，但有 jhdh，需要走 getJhByChe 反查
      if (!planJhdh) {
        Toast.fail('历史记录中未找到作业计划，请返回上一步重新选择计划');
        this.$set(msg, 'loadingSlots', false);
        this.$set(msg, 'slotsRequested', false);
        return;
      }

      await this.ensurePlatesFromHistory();

      const headNo = String(this.lastKnownHeadNo || '');
      const trailerNo = String(this.lastKnownTrailerNo || '').replace(/挂$/, '');
      const txhlb = this.jobType === 1 ? '卸货' : '提货';

      if (!headNo && !trailerNo) {
        Toast.fail('历史记录中未找到车牌号，请返回上一步重新上传行驶证');
        this.$set(msg, 'loadingSlots', false);
        this.$set(msg, 'slotsRequested', false);
        return;
      }

      console.log('[fillTimeSlotParamsFromHistory] 反查 getJhByChe 车头=', headNo, '车挂=', trailerNo, 'txhlb=', txhlb, '目标jhdh=', planJhdh);
      api.getJhByChe(headNo, trailerNo, txhlb)
        .then((resData) => {
          let r = resData;
          if (typeof r === 'string') r = JSON.parse(r);
          let plans = [];
          if (r && Array.isArray(r.plans)) plans = r.plans;
          else if (r && r.data && Array.isArray(r.data.plans)) plans = r.data.plans;
          else if (r && r.data && Array.isArray(r.data)) plans = r.data;
          const matched = plans.find((p) => String(p.jhdh || '') === planJhdh);
          console.log('[fillTimeSlotParamsFromHistory] 找到 plan=', matched ? matched.jhdh : 'null', 'thk=', matched ? String(matched.thk || matched.tkh || '') : '');
          if (!matched) {
            Toast.fail(`历史记录的计划单号 ${planJhdh} 在反查结果中不存在，请返回上一步重新选择计划`);
            this.$set(msg, 'loadingSlots', false);
            this.$set(msg, 'slotsRequested', false);
            return;
          }
          const thk = String(matched.thk || matched.tkh || '');
          if (!thk) {
            // 计划匹配到了但提货口字段为空：直接报具体缺什么，方便定位是后端字段缺失还是字段名不一致
            console.error('[fillTimeSlotParamsFromHistory] 计划缺少提货口(thk/tkh)字段，完整计划对象:', JSON.stringify(matched));
            Toast.fail(`计划 ${planJhdh} 的提货口信息缺失，请检查车头、车挂、计划信息是否匹配！`);
            this.$set(msg, 'loadingSlots', false);
            this.$set(msg, 'slotsRequested', false);
            return;
          }
          this.lastKnownPlanThk = thk;
          if (this.sessionId) {
            localStorage.setItem(`ai_plan_thk_${this.sessionId}`, thk);
          }
          this.selectedDate = finalDate;
          this.callGetPdxxNew(msg, thk, finalDate);
        })
        .catch((err) => {
          console.error('[fillTimeSlotParamsFromHistory] getJhByChe 失败', err);
          Toast.fail('查询作业计划失败');
          this.$set(msg, 'loadingSlots', false);
          this.$set(msg, 'slotsRequested', false);
        });
    },

    // choose_time 步骤：调 getpdxxnew 拉时间段列表
    // 入参：thk（选中的计划提货扣）、yyrq（第 6 步选中的预约日期）
    fetchTimeSlotsForMessage(msg) {
      if (msg.slotsRequested) {
        console.log('[fetchTimeSlotsForMessage] 已请求过，跳过');
        return;
      }
      this.$set(msg, 'slotsRequested', true);

      const localThk = String(this.lastKnownPlanThk || '');
      const localYyrq = String(this.selectedDate || '');
      console.log('[fetchTimeSlotsForMessage] 参数检查 thk=', localThk, 'yyrq=', localYyrq, 'msg=', msg);
      // 本地任一参数缺失（用户中途退出再进 / 选了日期没选计划等）→ 兜底从历史接口捞
      if (!localThk || !localYyrq) {
        this.fillTimeSlotParamsFromHistory(msg);
        return;
      }
      this.callGetPdxxNew(msg, localThk, localYyrq);
    },

    // 真正发 getpdxxnew 请求 + 处理响应（抽出来复用）
    callGetPdxxNew(msg, thk, yyrq) {
      api.getpdxxnew({ thk, yyrq })
        .then((resData) => {
          let res = resData;
          if (typeof res === 'string') res = JSON.parse(res);
          console.log('[fetchTimeSlotsForMessage] 调用 getpdxxnew 参数:', { thk, yyrq }, '返回:', res);
          // 兼容 respCode="0" / code=200 / data 直接是数组
          const codeRaw = res && (res.respCode !== undefined ? res.respCode : res.code);
          const code = String(codeRaw === undefined ? '' : codeRaw);
          const slotsArr = (res && Array.isArray(res.data) && res.data)
            || (res && Array.isArray(res) && res)
            || [];
          const ok = code === '0' || code === '200' || (res && res.ok === true) || slotsArr.length > 0;
          if (!ok) {
            Toast.fail((res && res.message) || '获取预约时间段失败');
            this.$set(msg, 'loadingSlots', false);
            this.$set(msg, 'slotsRequested', false);
            return;
          }
          // 把后端返回的 "06时15分-06时30分" 格式化成 "06:15-06:30"
// 入参支持 "HH时mm分" / "HH时mm分-HH时mm分" 两种
function formatTimeSlotText(pdsj) {
  if (!pdsj) return '';
  return String(pdsj).replace(/(\d+)\s*时\s*(\d+)\s*分/g, '$1:$2');
}

const slots = slotsArr
            .filter((it) => it && it.id)
            .map((it) => ({
              // 按钮显示文本：只显示 "HH:mm-HH:mm"，不要前面的年月日
              text: formatTimeSlotText(it.pdsj || ''),
              // 提交到后端的 value 用 id（与老 appointmentxhIndexNew.vue 一致）
              value: String(it.id),
              // 额外携带 thk / pdrq / pdsj，方便后端落库
              thk: String(it.thk || thk),
              pdrq: String(it.pdrq || ''),
              pdsj: String(it.pdsj || ''),
            }));
          if (!slots.length) {
            Toast.fail('当前日期没有可预约的时间段');
            this.$set(msg, 'loadingSlots', false);
            this.$set(msg, 'slotsRequested', false);
            return;
          }
          this.$set(msg, 'options', slots);
          this.$set(msg, 'loadingSlots', false);
          // 拿到时间段后，把 content 里残留的 "(该日期暂无可预约时段…)" / "（该日期暂无可预约时段…）" 这种错误提示清掉，
          // 否则即使接口有值，页面仍会显示该错误。括号兼容半角 / 全角。
          if (msg.content) {
            const cleaned = msg.content
              .replace(/[（(]该日期暂无可预约时段[^）)\n]*[)）]/g, '')
              .replace(/\n{3,}/g, '\n\n')
              .trim();
            if (cleaned !== msg.content) this.$set(msg, 'content', cleaned);
          }
          // 内容渲染完后自动滚到底部，让用户看到完整的时间段
          this.$nextTick(() => this.scrollToBottom());
        })
        .catch((err) => {
          console.error('[getpdxxnew] 失败', err);
          Toast.fail('获取预约时间段失败');
          this.$set(msg, 'loadingSlots', false);
          this.$set(msg, 'slotsRequested', false);
        });
    },

    // 车头/车挂行驶证 OCR 后确认车牌号，按 erp-process-ocr-frontend-api.md §1.3 推进
    async onPlateConfirm(action, done) {
      if (action !== 'confirm') {
        done();
        return;
      }
      const plateNo = this.confirmPlateNo.trim();
      if (!plateNo) {
        Toast.fail('请输入车牌号');
        done(false);
        return;
      }
      const user = this.getUserInfo();
      if (!user.openid) {
        Toast.fail('缺少 openid');
        done();
        return;
      }
      const ctx = this.pendingErpOcr;
      if (!ctx) {
        Toast.fail('会话上下文丢失，请重新上传');
        done();
        return;
      }
      console.log('[onPlateConfirm] sourceStep=', ctx.sourceStep, 'plateNo=', plateNo, 'isChangingHead=', this.isChangingHead, 'lastKnownTrailerNo=', this.lastKnownTrailerNo);

      // 校验车牌格式
      if (ctx.sourceStep.startsWith('vehicle_license_') && plateNo.endsWith('挂')) {
        Toast.fail('车头车牌号不能以"挂"字结尾');
        done(false);
        return;
      }
      if (ctx.sourceStep.startsWith('trailer_license_') && !plateNo.endsWith('挂')) {
        Toast.fail('车挂号必须以"挂"字结尾');
        done(false);
        return;
      }

      // 更新车牌号到 OCR 结果
      const ocrData = { ...ctx.ocrData, plateNo };
      Toast.loading({ message: '查询计划中...', duration: 0, forbidClick: true });
      try {
        // 重新查计划（用用户可能修改过的车牌号）
        const planInfo = await this.queryErpPlans(ctx.sourceStep, ocrData);
        // 【权威字段优先】phWhDriver === 'WEIHUO_NEW' → 危货新用户 → 跳 addAllCar
        if (planInfo && planInfo.phWhDriver === 'WEIHUO_NEW') {
          Toast.clear();
          done();
          this.$set(this.pendingTempMsg, 'verified', 'failed');
          // 新司机 AI 对话危货绑定流程开始，先重置标记，待 addAllCar 绑定完成后再置为 true
          this.$store.commit('set_aiChatVehicleBound', false);
          Dialog.alert({
            title: '提示',
            message: '系统检测到您是危货新用户，请先绑定车辆信息',
            confirmButtonText: '去绑定',
          }).then(() => {
            this.$router.push({ name: 'addAllCar', query: { source: 'aichat' } });
          });
          return;
        }
        if (planInfo === null || !planInfo.headPlan || !planInfo.headPlan.hasPlan) {
          Toast.clear();
          done();
          this.$set(this.pendingTempMsg, 'verified', 'failed');
          // 无计划时先判是否油脂车辆（进错对话），是则引导去丽天车队小程序；否则维持原有提示
          if (ctx.sourceStep === 'vehicle_license_front' && await this.checkOilVehicleByPlate(plateNo)) {
            this.showOilGuide();
            return;
          }
          this.pushServerMessage({
            type: 'robot',
            role: 'robot',
            content: '未查询到车辆计划信息，请联系客服进行车辆计划创建，无法继续预约。',
            isBlock: true,
          }, true);
          return;
        }
        if (planInfo.isPuhuo === false) {
          // 含危货计划 → 弹框 → 跳转绑定车辆页
          Toast.clear();
          done();
          this.$set(this.pendingTempMsg, 'verified', 'failed');
          // 新司机 AI 对话危货绑定流程开始，先重置标记，待 addAllCar 绑定完成后再置为 true
          this.$store.commit('set_aiChatVehicleBound', false);
          Dialog.alert({
            title: '提示',
            message: '系统检测到该车辆是危货，请先绑定车辆信息',
            confirmButtonText: '去绑定',
          }).then(() => {
            this.$router.push({ name: 'addAllCar', query: { source: 'aichat' } });
          });
          return;
        }

        // 查计划通过后才记录权威车牌号，失败的记录会污染副页交叉校验
        if (ctx.sourceStep === 'vehicle_license_front') {
          this.lastKnownHeadNo = plateNo.replace(/挂$/, '');
          this.isChangingHead = false; // 新车头已确认，后续可以正常用车头兜底查车挂
          const user = this.getUserInfo();
          if (user.openid) {
            localStorage.removeItem(`ai_changing_head_${user.openid}`);
          }
        } else if (ctx.sourceStep === 'trailer_license_front') {
          const clean = plateNo.replace(/挂$/, '');
          this.lastKnownTrailerNo = clean.endsWith('挂') ? clean : (clean + '挂');
        }
        // 按 sessionId / openid 落盘 localStorage：
        //   - sessionId：用于同一会话内退出再进
        //   - openid：用于会话 ID 变化（如切换页面后后端开了新会话）
        const lsUser = this.getUserInfo();
        if (this.sessionId && (this.lastKnownHeadNo || this.lastKnownTrailerNo)) {
          if (this.lastKnownHeadNo) localStorage.setItem(`ai_head_no_${this.sessionId}`, this.lastKnownHeadNo);
          if (this.lastKnownTrailerNo) localStorage.setItem(`ai_trailer_no_${this.sessionId}`, this.lastKnownTrailerNo);
        }
        if (lsUser.openid && (this.lastKnownHeadNo || this.lastKnownTrailerNo)) {
          if (this.lastKnownHeadNo) localStorage.setItem(`ai_head_no_${lsUser.openid}`, this.lastKnownHeadNo);
          if (this.lastKnownTrailerNo) localStorage.setItem(`ai_trailer_no_${lsUser.openid}`, this.lastKnownTrailerNo);
        }
        console.log('[onPlateConfirm] 已确定 sourceStep=', ctx.sourceStep, 'plateNo=', plateNo, 'lastKnownHeadNo=', this.lastKnownHeadNo, 'lastKnownTrailerNo=', this.lastKnownTrailerNo);

        // 【P5】popup 确认时也兜底：拿后端真实 currentStep，避免 ctx.sourceStep 与后端不一致。
        //   防御性：ctx.sourceStep 是 popup 触发时缓存的，可能已经过时。
        try {
          const sessResp = await erpDialogApi.getSession(this.sessionId, user.openid);
          if (sessResp && sessResp.code === 200 && sessResp.data && sessResp.data.currentStep) {
            this.sessionCurrentStep = sessResp.data.currentStep;
          }
        } catch (e) {
          // ignore：fallback 到 ctx.sourceStep
        }

        // 调 /processErpOcr 推进步骤
        const resp = await erpDialogApi.processErpOcr({
          openid: user.openid,
          sessionId: this.sessionId,
          // 【P5】优先用 sessionCurrentStep（与后端 DB 一致）；fallback 用 ctx.sourceStep
          sourceStep: this.sessionCurrentStep || ctx.sourceStep,
          ocrResult: ocrData,
          planInfo,
        });
        Toast.clear();
        if (resp.code !== 200 || !resp.data) {
          done();
          Toast.fail(resp.msg || '推进失败，请重试');
          return;
        }
        done();
        const botReply = resp.data.botReply;
        if (botReply) {
          this.pushServerMessage({
            ...botReply,
            currentStep: botReply.currentStep || resp.data.currentStep,
          });
        }
      } catch (err) {
        Toast.clear();
        console.error('[onPlateConfirm] 失败', err);
        done();
        Toast.fail(err.message || '网络异常，请稍后重试');
      }
    },
    // 把后端模板里可能返回的步骤编码统一转成 API 用的 sourceStep
    normalizeErpSourceStep(step) {
      const map = {
        vehicle_license_1: 'vehicle_license_front',
        vehicle_license_front: 'vehicle_license_front',
        vehicle_license_2: 'vehicle_license_back',
        vehicle_license_back: 'vehicle_license_back',
        vehicle_license_3: 'vehicle_license_copy',
        vehicle_license_copy: 'vehicle_license_copy',
        trailer_license_1: 'trailer_license_front',
        trailer_license_front: 'trailer_license_front',
        trailer_license_2: 'trailer_license_back',
        trailer_license_back: 'trailer_license_back',
        trailer_license_3: 'trailer_license_copy',
        trailer_license_copy: 'trailer_license_copy',
        driver_license_1: 'driver_license_front',
        driver_license_front: 'driver_license_front',
      };
      return map[step] || step;
    },
    // 【ERP 模式】用后端 botReply.currentStep 作为 sourceStep。
    // 后端推进后 currentStep 会切到下一步，所以该值要实时读。
    detectErpSourceStep() {
      const lastMsg = this.messageList[this.messageList.length - 1];
      if (lastMsg && lastMsg.type === 'robot' && lastMsg.currentStep) {
        return this.normalizeErpSourceStep(lastMsg.currentStep);
      }
      // 兜底：用消息内容关键词推断
      const content = (lastMsg && lastMsg.content) || '';
      // 副本反页/副本正页更具体，优先判断，避免被"正页/副页"短关键词误覆盖
      if (content.includes('车头行驶证副本反页') || content.includes('车头行驶证副本反面')) return 'vehicle_license_copy';
      if (content.includes('车头行驶证副本正页') || content.includes('车头行驶证副本正面')) return 'vehicle_license_back';
      if (content.includes('车头行驶证正页') || content.includes('车头行驶证正面')) return 'vehicle_license_front';
      if (content.includes('车头行驶证副页') || content.includes('车头行驶证反面')) return 'vehicle_license_back';
      if (content.includes('车挂行驶证副本反页') || content.includes('车挂行驶证副本反面')) return 'trailer_license_copy';
      if (content.includes('车挂行驶证副本正页') || content.includes('车挂行驶证副本正面')) return 'trailer_license_back';
      if (content.includes('车挂行驶证正页') || content.includes('车挂行驶证正面')) return 'trailer_license_front';
      if (content.includes('车挂行驶证副页') || content.includes('车挂行驶证反面')) return 'trailer_license_back';
      if (content.includes('驾驶证正页') || content.includes('驾驶证正面')) return 'driver_license_front';
      return 'vehicle_license_front';
    },
    // 【ERP 模式】行驶证步骤需要 cardSide=FRONT/BACK；副本反页不需要
    detectErpCardSide(sourceStep) {
      if (sourceStep === 'vehicle_license_front' || sourceStep === 'trailer_license_front') return 'FRONT';
      if (sourceStep === 'vehicle_license_back' || sourceStep === 'trailer_license_back') return 'BACK';
      return null;
    },
    detectExampleImage(content) {
      if (!content) return undefined;
      if (content.includes('车头行驶证正页') || content.includes('车头行驶证正面') || content.includes('车挂行驶证正页') || content.includes('车挂行驶证正面')) {
        return 'https://shltyy.jsltpct.com:9526/upload/lt-pubilc/public-resources/车头行驶证正页.png';
      }
      if (content.includes('车挂行驶证副本正页') || content.includes('车挂行驶证副本正面')) {
        return 'https://shltyy.jsltpct.com:9526/upload/lt-pubilc/public-resources/车头行驶证副本正页.png';
      }
      // 车头行驶证副页：暂用同一张车挂行驶证副页示例图
      if (content.includes('车头行驶证副本正页') || content.includes('车头行驶证副本正面')) {
        return 'https://shltyy.jsltpct.com:9526/upload/lt-pubilc/public-resources/车头行驶证副本正页.png';
      }
      if (content.includes('车头行驶证副本反页') || content.includes('车头行驶证副本反面') || content.includes('车挂行驶证副本反页') || content.includes('车挂行驶证副本反面')) {
        return 'https://shltyy.jsltpct.com:9526/upload/lt-pubilc/public-resources/车头行驶证副本反页.png';
      }
      if (content.includes('驾驶证正页') || content.includes('驾驶证正面')) {
        return 'https://shltyy.jsltpct.com:9526/upload/lt-pubilc/public-resources/驾驶证正页.jpg';
      }
      return undefined;
    },
    previewImage(url) {
      // 预览必须也用签名 URL，否则 vant-image-preview 大图也是 403
      const apply = (signed) => {
        this.previewImages = [signed || url];
        this.previewIndex = 0;
        this.previewShow = true;
      };
      // 非 http(s)（blob / data:）直接打开
      if (!/^https?:\/\//i.test(url)) { apply(url); return; }
      this.resolveImageUrl(url).then(apply).catch(() => apply(url));
    },

    // ---------- 功能面板 ----------
    toggleFuncPanel() {
      this.isShowFuncPanel = !this.isShowFuncPanel;
      this.scrollToBottom();
    },
    requestHint() {
      if (!this.checkActionCooldown()) return;
      if (!this.sessionId) return;
      Toast.loading({ message: '获取提示中...', duration: 0, forbidClick: true });
      const user = this.getUserInfo();
      this.chatApi.getHint(this.sessionId, user.openid).then((res) => {
        Toast.clear();
        if (res.code === 200 && res.data) this.pushServerMessage(res.data, true);
      }).catch((err) => {
        Toast.clear();
        Toast.fail('获取提示失败');
        console.error(err);
      });
    },
    handleGoBack() {
      if (!this.checkActionCooldown()) return;
      this.goBackErpAndRender();
    },
    // 统一走后端 goBack 接口并清理本地消息，供输入框回退关键词和功能面板共用
    goBackErpAndRender() {
      // 点击"上一步"时不再收起功能区，保留用户上次的展开状态
      // 调后端 goBack 接口拿到 botReply 直接渲染
      const user = this.getUserInfo();
      if (!user.openid || !this.sessionId) {
        Toast.fail('缺少会话信息，无法返回上一步');
        return;
      }
      Toast.loading({ message: '返回上一步...', duration: 0, forbidClick: true });
      erpDialogApi.goBackErp({ openid: user.openid, sessionId: this.sessionId })
        .then((res) => {
          Toast.clear();
          if (res.code !== 200 || !res.data) {
            Toast.fail(res.msg || '返回上一步失败');
            return;
          }
          const botReply = res.data.botReply;
          if (botReply && botReply.content) {
            const targetStep = res.data.currentStep;
            // 把目标 step 之后的所有机器人消息本地清掉，避免下一步 botReply
            // 被 dedup 命中到一条旧的、已 verified、且 scroll-off 的同 currentStep 消息上
            // 导致页面底部没有出现新的可交互按钮
            if (targetStep) {
              const targetIdx = ERP_STEP_ORDER.indexOf(targetStep);
              this.messageList = this.messageList.filter((m) => {
                if (m.type === 'user') return true;
                if (m.role === 'system') return true;
                if (!m.currentStep) return true;
                const idx = ERP_STEP_ORDER.indexOf(m.currentStep);
                if (idx === -1) return true;
                return idx <= targetIdx;
              });
            }
            this.pushServerMessage({
              ...botReply,
              currentStep: targetStep,
            }, true);
          }
        })
        .catch((err) => {
          Toast.clear();
          console.error('[goBack] 失败', err);
          Toast.fail(err.message || '返回上一步失败');
        });
    },
    handleRestart() {
      if (!this.checkActionCooldown()) return;
      this.doRestart(true, true);
    },
    handleReEnter() {
      this.doRestart(false, false);
    },
    async doRestart(needConfirm, showLoading) {
      if (needConfirm) {
        try {
          await Dialog.confirm({ message: '重新开始将结束当前会话，是否继续？' });
        } catch {
          return;
        }
      }
      if (this.sessionId) {
        if (showLoading) Toast.loading({ message: '正在结束会话...', duration: 0, forbidClick: true });
        try {
          const user = this.getUserInfo();
          await this.chatApi.closeSession(this.sessionId, user.openid);
        } catch (err) {
          console.error('[Restart] 关闭会话失败', err);
        } finally {
          if (showLoading) Toast.clear();
        }
      }
      this.closeWebSocket();
      this.stopReviewPolling();
      this.clearCache();
      this.sessionId = '';
      this.sessionStatus = '';
      this.messageList = [];
      this.orderList = [];
      this.isChangingHead = false;
      // 重置提交状态：避免上一次请求还在飞行中时点"重新开始"造成状态交叉
      this.isSubmitting = false;
      this.initSession(true);
    },
    goOrders() {
      if (!this.checkActionCooldown()) return;
      this.$router.push({ name: 'orderlist' });
    },

    // ---------- 审核轮询 ----------
    startReviewPolling() {
      this.stopReviewPolling();
      this.reviewPollTimer = setInterval(() => {
        if (!this.sessionId || this.sessionStatus !== 'waiting_review') return;
        const user = this.getUserInfo();
        this.chatApi.getReviewStatus(this.sessionId, user.openid).then((res) => {
          if (res.code !== 200 || !res.data) return;
          const status = res.data.reviewStatus;
          if (status === 'APPROVED') {
            this.stopReviewPolling();
            this.handleServerMessage({ type: 'review_approved', content: '您的资料已审核通过' });
          } else if (status === 'REJECTED') {
            this.stopReviewPolling();
            this.handleServerMessage({
              type: 'review_rejected',
              rejectReason: res.data.reviewRemark,
              rejectStep: res.data.rejectStep,
            });
          }
        }).catch(() => {});
      }, 5000);
    },
    stopReviewPolling() {
      if (this.reviewPollTimer) {
        clearInterval(this.reviewPollTimer);
        this.reviewPollTimer = null;
      }
    },

    // 把后端原始 OSS URL 换成可显示的签名 URL（私有桶 ruoyicould 必须换票）
    // - 非 http(s)（blob / data:）：原样返回（如本地预览）
    // - 非本桶（旧图床、外链、示例图 /upload/ruoyi/public/...）：原样返回
    // - 本会话已换票过的：直接返回缓存
    // - 换票失败：回落原 URL（不阻塞渲染）
    async resolveImageUrl(url) {
      if (!url) return url;
      if (!/^https?:\/\//i.test(url)) return url;
      if (!/\/upload\/ruoyicould(?:-prod)?\//i.test(url)) return url;

      // 【开关】后端 /shltyy/file/presign 当前只认登录 token，公众号 H5 没有登录 token，
      // 所以暂时禁用换票，直接返回原 URL，避免无意义的 401 请求。
      // 等后端支持 openid / dhyytoken 鉴权后再把下面开关改成 true。
      const PRESIGN_ENABLED = false;
      if (!PRESIGN_ENABLED) return url;

      if (this.imageUrlMap && this.imageUrlMap.has(url)) {
        return this.imageUrlMap.get(url);
      }
      try {
        const signed = await presignImage(url);
        if (this.imageUrlMap) this.imageUrlMap.set(url, signed);
        return signed;
      } catch (err) {
        console.error('[resolveImageUrl] 换票失败', url, err);
        return url;
      }
    },
    // ---------- 工具方法 ----------
    formatTime(date) {
      try {
        const h = String(date.getHours()).padStart(2, '0');
        const m = String(date.getMinutes()).padStart(2, '0');
        const s = String(date.getSeconds()).padStart(2, '0');
        return `${h}:${m}:${s}`;
      } catch (e) {
        return '';
      }
    },
    formatDateStr(date) {
      const d = date instanceof Date ? date : new Date(date);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    },
    calendarFormatter(day) {
      const msg = this.calendarMsgIndex !== null ? this.messageList[this.calendarMsgIndex] : null;
      if (msg && msg.availableDates && msg.availableDates.length) {
        const dateStr = this.formatDateStr(day.date);
        if (!msg.availableDates.includes(dateStr)) {
          return { ...day, type: 'disabled' };
        }
      }
      return day;
    },
    toMs(value) {
      if (value === null || value === undefined || value === '') return null;
      const num = typeof value === 'string' ? Number(value) : value;
      if (!Number.isFinite(num) || num <= 0) return null;
      return num < 1e12 ? num * 1000 : num;
    },
    getStatusTip() {
      if (this.sessionStatus === 'waiting_review') return '您的资料已提交审核，请耐心等待';
      if (this.sessionStatus === 'expired') return '会话已过期，请重新进入';
      return '';
    },
    listenKeyboard() {
      // 计算键盘高度：差值小于 150px 视为微信工具栏收起/视口抖动，不算键盘，
      // 避免首次冷启动时 visualViewport 的瞬时 resize 把输入框顶起来
      const calcKeyboardHeight = (h) => {
        if (!this.initialHeight) return 0;
        // 视口变高（工具条收起）时同步抬高基线，防止进页面时 initialHeight 记小了
        if (h > this.initialHeight) this.initialHeight = h;
        const diff = this.initialHeight - h;
        return diff > 150 ? diff : 0;
      };
      this.onResize = () => {
        this.keyboardHeight = calcKeyboardHeight(window.innerHeight);
        this.scrollToBottom();
      };
      this.initialHeight = window.innerHeight;
      window.addEventListener('resize', this.onResize);
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', () => {
          this.keyboardHeight = calcKeyboardHeight(window.visualViewport.height);
          this.scrollToBottom();
        });
      }
    },
    onInputFocus() {
      this.isShowFuncPanel = false;
      this.scrollToBottom();
    },
    scrollToBottom() {
      this.$nextTick(() => {
        setTimeout(() => {
          const ref = this.$refs.messageListRef;
          if (ref) ref.scrollTop = ref.scrollHeight;
        }, 100);
      });
    },
    // 滚动到指定 index 的消息，让用户能看到 dedup 命中并被更新的那条消息
    // 用于 dedup 把早期 WS 占位 text 升级成 time_slot / choice 等需要前端再拉数据 UI 的场景
    scrollToMessage(index) {
      this.$nextTick(() => {
        setTimeout(() => {
          const ref = this.$refs.messageListRef;
          if (!ref) return;
          const el = document.getElementById('msg-' + index);
          if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          } else {
            ref.scrollTop = ref.scrollHeight;
          }
        }, 100);
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.ai-chat-page {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #f0f7ff;
  overflow: hidden;
}
.status-bar {
  padding: 8px 12px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
  justify-content: center;
}
.status-review { background: #fffbe8; color: #ff976a; }
.status-expired { background: #ffe5e5; color: #ee0a24; }
.chat-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}
.message-list {
  flex: 1;
  overflow-y: auto;
  padding: 20px 20px 0;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
}
.message-wrapper {
  margin-bottom: 20px;
}
.message-time {
  text-align: center;
  font-size: 12px;
  color: #999;
  margin-bottom: 6px;
}
.input-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  background: #fff;
  padding: 8px 12px;
  border-top: 1px solid #eee;
  z-index: 100;
  padding-bottom: calc(8px + env(safe-area-inset-bottom));
}
.input-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.input-row .van-field {
  flex: 1;
  padding: 6px 10px;
  background: #f5f5f5;
  border-radius: 18px;
}
.func-btn {
  min-width: 36px;
}
.order-display-mode {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  padding-bottom: 80px;
}
.order-mode-header {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 12px;
  color: #333;
}
</style>
